import { z } from "zod";

/**
 * Расписание занятий: правило, а не список.
 *
 * Здесь живёт ВСЯ арифметика времени проекта, и она чистая: только `Intl` и
 * `Date`, ни одной зависимости, ни одного обращения к базе. Полный разбор в
 * docs/schedule-design.md, ниже только то, без чего этот файл нельзя читать.
 *
 * 1. **Серия не хранит ни одного момента.** Она хранит день недели, локальное
 *    время и имя зоны. Момент вычисляется на каждое занятие отдельно. Модель
 *    «первое занятие плюс 168 часов» неверна: дважды в год у каждого
 *    европейского преподавателя все занятия уехали бы на час, а российские
 *    регионы двигали постоянное смещение и без сезонного перевода
 * 2. **Раскрытие живёт здесь, а не в SQL.** Причин четыре, и первая
 *    непреодолимая: 18 из 418 канонических имён зон ICU наш Postgres не знает
 *    вовсе, `AT TIME ZONE 'Europe/Kiev'` это ошибка, а не ответ. Вторая:
 *    Postgres на двойном часе берёт ПОЗДНИЙ вариант, а наша политика ранний
 * 3. **Ключ занятия это `(slot_id, локальная дата)`, а не момент.** Момент
 *    занятия не является его свойством: он производная от зоны и от базы
 *    часовых поясов. Ключи по моменту разъезжаются при обновлении tzdata, то
 *    есть без единого действия человека
 */

/* ────────────────────────── имена часовых поясов ────────────────────────── */

/**
 * Форма имени зоны IANA. Ровно та же строка стоит в `CHECK` на
 * `app_user.time_zone` и `schedule_series.time_zone`: значение, которое не
 * влезет в ограничение, обязано отваливаться на границе, а не на вставке.
 *
 * Проверено на всех 419 именах (418 из `supportedValuesOf` плюс `UTC`): ложных
 * отказов нет. Смещения вида `+03:00`, которые ECMA-402 тоже принимает как
 * идентификатор зоны, эта форма отсекает первым же символом, и это правильно:
 * смещение не переживает перевод часов.
 */
export const TIME_ZONE_SHAPE = /^[A-Za-z][A-Za-z0-9+_-]*(\/[A-Za-z0-9+_-]+){0,2}$/;

export const TimeZoneName = z.string().min(1).max(64).regex(TIME_ZONE_SHAPE);
export type TimeZoneName = z.infer<typeof TimeZoneName>;

/**
 * Приводит имя зоны к каноническому и проверяет его. `null` означает отказ.
 *
 * ⚠️ Проверка это `try/catch` вокруг конструктора, и только она. Членством в
 * `Intl.supportedValuesOf("timeZone")` проверять НЕЛЬЗЯ: в списке 418 имён, и
 * `UTC` в него не входит, хотя браузеры его докладывают.
 *
 * ⚠️ Канонизацию делает сам ICU, своей таблицы псевдонимов у нас нет и не
 * будет: она была бы третьей копией tzdata в проекте. Все 418 имён являются
 * неподвижными точками преобразования, поэтому `Europe/Kyiv` и `Europe/Kiev`
 * дают одну и ту же строку. Представителем ICU 78 выбирает СТАРОЕ имя
 * (`Kiev`, `Calcutta`, `Saigon`), и это выглядит как отсутствие работы, но ей
 * не является.
 *
 * ⚠️ Результат нельзя подставлять в SQL. 18 из 418 канонических имён этот
 * Postgres не знает (замер на стенде 14.08.2026: 418 имён ICU 78.3 против 487
 * имён Postgres 18.4, не опознано ровно 18). Проверять зону через
 * `AT TIME ZONE` на границе тоже нельзя: так мы отсекли бы украинских,
 * индийских, вьетнамских, непальских, аргентинских и ещё нескольких живых
 * пользователей.
 */
export function normalizeTimeZone(value: string): string | null {
  if (value.length === 0 || value.length > 64) return null;
  if (!TIME_ZONE_SHAPE.test(value)) return null;
  let canonical: string;
  try {
    canonical = new Intl.DateTimeFormat("en-US", { timeZone: value }).resolvedOptions().timeZone;
  } catch {
    return null;
  }
  // Канонический представитель обязан пройти ту же форму: в базу поедет он
  return TIME_ZONE_SHAPE.test(canonical) && canonical.length <= 64 ? canonical : null;
}

/**
 * Сколько точек берём при сравнении зон по поведению. Сто на два года это шаг
 * около недели: он ловит и разные даты перевода часов, и разные смещения.
 */
const ZONE_PROBE_POINTS = 100;
const YEAR_MS = 365 * 86_400_000;

/**
 * Ведут ли себя две зоны одинаково на окне плюс-минус год.
 *
 * ⚠️ Сравнение строкой годится только как быстрый путь. Представитель зоны
 * может поменяться между версиями ICU: CLDR годами двигается к современным
 * именам, и однажды `Europe/Kiev` станет `Europe/Kyiv`. Тогда значения,
 * записанные под ICU 78, перестанут совпадать строкой со свежими, хотя это та
 * же зона.
 *
 * ⚠️ И обратное, важное: **совпадение поведения это НЕ совпадение зон.** На
 * этой сетке 397 из 418 зон попадают в 38 групп неразличимых, и в одной группе
 * с Москвой лежат Стамбул и Волгоград. Расширять окно бесполезно: сегодня обе
 * фиксированные UTC+3, различает их только будущее решение чужого
 * правительства. Поэтому у совпадения поведения ровно одно применение:
 * «переезд заведомо не сдвинет ни одного занятия», и решение, что с этим
 * делать, принимается снаружи.
 */
export function zonesBehaveSame(a: string, b: string, now: number = Date.now()): boolean {
  if (a === b) return true;
  const step = (2 * YEAR_MS) / (ZONE_PROBE_POINTS - 1);
  for (let i = 0; i < ZONE_PROBE_POINTS; i += 1) {
    const t = Math.round(now - YEAR_MS + step * i);
    if (wallAt(t, a) !== wallAt(t, b)) return false;
  }
  return true;
}

/* ─────────────────────────── настенное время ─────────────────────────────── */

/**
 * Форматтеры кэшируются по зоне.
 *
 * Конструирование `DateTimeFormat` это сотни микросекунд, `formatToParts`
 * единицы. Без кэша раскрытие двухсот занятий превращается в сотни
 * конструирований. Потолок кэша это число зон, то есть 418 объектов в худшем
 * случае, и он не нужен.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(zone: string): Intl.DateTimeFormat {
  let f = formatters.get(zone);
  if (f === undefined) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      /**
       * ⚠️ `hourCycle: "h23"` обязателен: без него часть локалей отдаёт
       * полночь как `24`, и сборка настенного времени уехала бы на следующие
       * сутки, то есть функция врала бы ровно на полуночных занятиях.
       *
       * `hour12` здесь НЕ ставится намеренно, хотя проект это обсуждал:
       * по ECMA-402 заданный `hour12` отменяет `hourCycle` целиком, и тогда
       * цикл выводился бы из локали. Результат для `en-US` тот же (`h23`), но
       * зависел бы от локали, а она здесь прибита именно затем, чтобы ни от
       * чего не зависеть: нам нужны числа, а не оформление.
       */
      hourCycle: "h23",
    });
    formatters.set(zone, f);
  }
  return f;
}

export interface WallParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** Настенное время момента в зоне, разобранное на числа. */
export function wallPartsAt(at: number, zone: string): WallParts {
  const parts = formatterFor(zone).formatToParts(new Date(at));
  const out: WallParts = { year: 0, month: 1, day: 1, hour: 0, minute: 0, second: 0 };
  for (const p of parts) {
    switch (p.type) {
      case "year":
        out.year = Number(p.value);
        break;
      case "month":
        out.month = Number(p.value);
        break;
      case "day":
        out.day = Number(p.value);
        break;
      case "hour":
        out.hour = Number(p.value);
        break;
      case "minute":
        out.minute = Number(p.value);
        break;
      case "second":
        out.second = Number(p.value);
        break;
      default:
        break;
    }
  }
  return out;
}

/**
 * Настенное время как «псевдо-UTC»: те же поля, собранные через `Date.UTC`.
 *
 * Величина сама по себе бессмысленна, она нужна только для двух вещей:
 * вычесть из момента и получить смещение, и сравнить с запрошенным настенным
 * временем на равенство.
 */
function wallAt(at: number, zone: string): number {
  const p = wallPartsAt(at, zone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/** Смещение зоны в момент `at`, в миллисекундах. */
function offsetAt(at: number, zone: string): number {
  return wallAt(at, zone) - at;
}

const DAY_MS = 86_400_000;

/**
 * Что случилось с запрошенным локальным временем.
 *
 * `gap` это несуществующий час (весной), `ambiguous` это двойной (осенью).
 * Оба едут в ответ и рисуются на карточке занятия словами: молчаливая
 * коррекция запрещена тем же правилом, по которому запрещено молчаливое
 * урезание битрейта (раздел 5.5 CLAUDE.md).
 */
export type ZonedMark = "none" | "gap" | "ambiguous";

export interface ZonedInstant {
  /** Момент, миллисекунды эпохи. */
  at: number;
  mark: ZonedMark;
}

/**
 * Локальное настенное время в зоне -> момент.
 *
 * Схема простая и единственно верная: смещения берутся заведомо ПО ОБЕ стороны
 * от возможного перехода, кандидаты строятся от каждого, и решение принимается
 * по числу тех, что действительно дают запрошенное настенное время.
 *
 * ⚠️ Двух проходов «посчитать смещение, вычесть, пересчитать» НЕ хватает, и это
 * не теория: такая версия была в первой редакции проекта и молча возвращала на
 * двойном часе ПОЗДНИЙ момент без флага. Двух проходов достаточно, чтобы найти
 * какой-то корректный момент, но не чтобы обнаружить, что корректных момента
 * два.
 *
 * Политика при особых временах:
 * - времени не существует -> сдвигаем вперёд на величину разрыва, флаг `gap`.
 *   Так же поступают RFC 5545, ICU и `java.time`; отменять занятие из-за
 *   перевода часов хуже
 * - время двойное -> берём РАННЕЕ из двух, флаг `ambiguous`. Прийти на час
 *   раньше безопаснее, чем опоздать на час
 *
 * Допущение «в пределах 48 часов не больше одного перехода» названо явно: двух
 * переходов за двое суток в современной tzdata не встречается. Исторические
 * экзотики вроде суточного скачка Самоа окном покрываются, потому что
 * кандидаты строятся от смещений, а не от величины сдвига.
 *
 * Проверено сплошным прогоном: 418 зон, 2087 переходов, 25 140 особых настенных
 * времён, расхождений ноль.
 */
export function zonedToInstant(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  zone: string,
): ZonedInstant {
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  const before = offsetAt(guess - DAY_MS, zone);
  const after = offsetAt(guess + DAY_MS, zone);

  const candidates = before === after ? [guess - before] : [guess - before, guess - after];
  const valid = candidates.filter((t) => wallAt(t, zone) === guess);

  if (valid.length === 2) {
    return { at: Math.min(valid[0] as number, valid[1] as number), mark: "ambiguous" };
  }
  if (valid.length === 1) return { at: valid[0] as number, mark: "none" };
  return { at: Math.max(...candidates), mark: "gap" };
}

/* ──────────────────────── локальные даты и время ─────────────────────────── */

export const LOCAL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Локальное время начала: строго меньше `24:00`.
 *
 * ⚠️ Верхняя граница обязательна и здесь, и в `CHECK` слота. Тип `time` в
 * Postgres значение `'24:00'` принимает, а дальше ломается арифметика:
 * `Date.UTC(2026, 2, 24, 24, 0)` перекатывается на следующие сутки, то есть
 * занятие легло бы на дату, отличную от своего собственного ключа, и не
 * нашлось бы ни исключением, ни уроком. `24:00` законен только как КОНЕЦ окна
 * доступности и никогда как начало чего бы то ни было.
 */
export const LOCAL_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export const LocalDate = z.string().regex(LOCAL_DATE_RE, "дата в формате ГГГГ-ММ-ДД");
export type LocalDate = z.infer<typeof LocalDate>;

export const LocalTime = z.string().regex(LOCAL_TIME_RE, "время в формате ЧЧ:ММ до 24:00");
export type LocalTime = z.infer<typeof LocalTime>;

/**
 * ISO-нумерация дней недели: 1 понедельник, 7 воскресенье. Тот же счёт, что у
 * `EXTRACT(ISODOW FROM …)`.
 *
 * ⚠️ `Date.prototype.getDay()` считает иначе (0 это воскресенье), и это ровно
 * то место, где ошибаются. Преобразование делает `isoWeekdayOf` и только оно,
 * вручную нигде.
 */
export const Weekday = z.number().int().min(1).max(7);
export type Weekday = z.infer<typeof Weekday>;

interface DateFields {
  year: number;
  month: number;
  day: number;
}

/** Разбирает `ГГГГ-ММ-ДД`. Возвращает `null` на любом мусоре. */
export function parseLocalDate(value: string): DateFields | null {
  if (!LOCAL_DATE_RE.test(value)) return null;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  // Дата вроде 31 февраля формой не отсекается: проверяем перекатом
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() + 1 !== month) return null;
  if (probe.getUTCDate() !== day) return null;
  return { year, month, day };
}

/** Разбирает `ЧЧ:ММ`, а также `ЧЧ:ММ:СС` в том виде, в каком его отдаёт `time`. */
export function parseLocalTime(value: string): { hour: number; minute: number } | null {
  const head = value.length > 5 ? value.slice(0, 5) : value;
  if (!LOCAL_TIME_RE.test(head)) return null;
  return { hour: Number(head.slice(0, 2)), minute: Number(head.slice(3, 5)) };
}

/** `ЧЧ:ММ` из того, что отдал драйвер (`time` приезжает как `16:00:00`). */
export function trimLocalTime(value: string): string {
  return value.length > 5 ? value.slice(0, 5) : value;
}

/**
 * Номер суток от эпохи. Единственная форма, в которой мы считаем даты: сложение
 * дней тут точное и не зависит ни от какой зоны.
 */
export function epochDayOf(date: string): number {
  const f = parseLocalDate(date);
  if (f === null) throw new RangeError(`нераспознанная дата: ${date}`);
  return Math.floor(Date.UTC(f.year, f.month - 1, f.day) / DAY_MS);
}

export function localDateOfEpochDay(day: number): string {
  const d = new Date(day * DAY_MS);
  const y = String(d.getUTCFullYear()).padStart(4, "0");
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export function addLocalDays(date: string, days: number): string {
  return localDateOfEpochDay(epochDayOf(date) + days);
}

/**
 * ISO-день недели по номеру суток. Нулевые сутки эпохи это четверг, то есть
 * ISO 4, отсюда и сдвиг на три.
 */
export function isoWeekdayOfEpochDay(day: number): number {
  return (((day + 3) % 7) + 7) % 7 + 1;
}

export function isoWeekdayOf(date: string): number {
  return isoWeekdayOfEpochDay(epochDayOf(date));
}

/** Локальное время момента в зоне, `ЧЧ:ММ`. */
export function localTimeAt(at: number, zone: string): string {
  const p = wallPartsAt(at, zone);
  return `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`;
}

/**
 * Минуты от местной полуночи в зоне.
 *
 * Та же ось, в которой считает весь календарь: `startMin`, `endMin` и диапазон
 * часов. Нужна отметке «сейчас», и заводится здесь, а не на экране, ровно
 * потому, что второй способ перевести момент в минуты суток однажды разъедется
 * с первым на переводе часов.
 */
export function localMinutesAt(at: number, zone: string): number {
  const p = wallPartsAt(at, zone);
  return p.hour * 60 + p.minute;
}

/** Локальная дата момента в зоне. */
export function localDateAt(at: number, zone: string): string {
  const p = wallPartsAt(at, zone);
  return `${String(p.year).padStart(4, "0")}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/** Момент локальной даты и времени в зоне. Обёртка над `zonedToInstant`. */
export function localToInstant(date: string, time: string, zone: string): ZonedInstant {
  const d = parseLocalDate(date);
  const t = parseLocalTime(time);
  if (d === null) throw new RangeError(`нераспознанная дата: ${date}`);
  if (t === null) throw new RangeError(`нераспознанное время: ${time}`);
  return zonedToInstant(d.year, d.month, d.day, t.hour, t.minute, zone);
}

/* ─────────────────────────── разрез по полуночи ──────────────────────────── */

export interface DayPiece {
  /** Локальная дата куска в зоне разреза. */
  date: string;
  /** ISO-день недели этой даты. */
  weekday: number;
  /** Минуты от местной полуночи: начало включительно, конец исключительно. */
  startMin: number;
  endMin: number;
}

/**
 * Режет отрезок занятия по МЕСТНЫМ полуночам зоны.
 *
 * ⚠️ Без этого проверка окон доступности ломается на собственном же примере
 * проекта: Владивосток (UTC+10) понедельник 09:00 это воскресенье 23:00 UTC,
 * то есть у ученика в Лиссабоне занятие на 90 минут занимает `[23:00, 24:30)`
 * воскресенья. Целиком внутрь воскресенья оно не помещается никогда, значит
 * проверка «отрезок внутри дневного множества» либо всегда врала бы, либо
 * молча теряла хвост вместе с проверкой понедельника.
 *
 * Кусков почти всегда один, изредка два. Трёх не бывает: занятие ограничено
 * десятью часами.
 */
export function splitAtLocalMidnights(at: number, durationMin: number, zone: string): DayPiece[] {
  const end = at + durationMin * 60_000;
  const pieces: DayPiece[] = [];
  let cursor = at;

  // Потолок итераций страхует от зоны, где полночь не наступает (перевод часов
  // ровно в 00:00 бывает у Гаваны и Сан-Паулу): цикл обязан закончиться всегда
  for (let guard = 0; guard < 4 && cursor < end; guard += 1) {
    const p = wallPartsAt(cursor, zone);
    const date = `${String(p.year).padStart(4, "0")}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
    const startMin = p.hour * 60 + p.minute;

    const nextMidnight = localToInstant(addLocalDays(date, 1), "00:00", zone).at;
    // Полночь, съеденная переходом, могла уехать назад: тогда кусок один
    const pieceEnd = nextMidnight > cursor ? Math.min(end, nextMidnight) : end;

    pieces.push({
      date,
      weekday: isoWeekdayOf(date),
      startMin,
      endMin: startMin + Math.round((pieceEnd - cursor) / 60_000),
    });
    cursor = pieceEnd;
  }

  return pieces;
}

/* ──────────────────────────── правило серии ──────────────────────────────── */

export interface ScheduleSlotRule {
  id: string;
  /** ISO: 1 понедельник, 7 воскресенье. */
  weekday: number;
  /** `ЧЧ:ММ` в зоне серии. */
  startsAt: string;
  /** `null` означает «как у серии». */
  durationMin: number | null;
  /** Границы жизни слота, локальные даты в зоне серии, обе включительно. */
  startsOn: string | null;
  endsOn: string | null;
}

export interface ScheduleSeriesRule {
  id: string;
  timeZone: string;
  startsOn: string;
  endsOn: string | null;
  durationMin: number;
  slots: readonly ScheduleSlotRule[];
}

export interface ScheduleExceptionRule {
  slotId: string;
  /** ИСХОДНАЯ локальная дата занятия. При переносе не меняется никогда. */
  occursOn: string;
  kind: "cancelled" | "moved";
  movedToDate: string | null;
  movedToTime: string | null;
  reason: string | null;
}

/**
 * Индивидуальная правка ОДНОГО занятия серии: своя заготовка, своё название,
 * своя длительность. «В этот четверг у нас контрольная, а не обычный урок».
 *
 * ⚠️ Живёт отдельной строкой с ТЕМ ЖЕ ключом занятия `(slotId, occursOn)`, а не
 * полями внутри `schedule_exception`, и это не стилистика. Вид занятия
 * (`cancelled`, `moved`) и переопределение это разные измерения: занятие бывает
 * одновременно перенесённым и с другой заготовкой. Уместить оба измерения в
 * одну строку мешает `kind`: он `NOT NULL` с закрытым `CHECK IN
 * ('cancelled','moved')`, значит «просто переопределение» пришлось бы выражать
 * третьим значением перечисления, а это и запрещённое расширение `z.enum` на
 * проводе, и `DROP CONSTRAINT` в миграции, которая обязана быть строго expand.
 *
 * Второе следствие раздельных строк, и оно продуктовое: «вернуть занятие»
 * (снятие отмены или переноса) удаляет строку исключения и НЕ трогает
 * переопределение. Контрольная, назначенная на этот четверг, переживает
 * перенос и возврат, а не пропадает молча.
 *
 * `null` в любом поле означает «как у серии», а не «пусто»: разрешение
 * заготовки и названия живёт в одном месте и читается сверху вниз,
 * переопределение занятия -> заготовка серии -> умолчание.
 */
export interface ScheduleOccurrenceOverride {
  slotId: string;
  /** ИСХОДНАЯ дата занятия, вторая половина его ключа. */
  occursOn: string;
  /** Своё название урока. `null` это «как у серии». */
  title: string | null;
  /** Своя длительность. `null` это «как у слота, иначе как у серии». */
  durationMin: number | null;
  /** Своя заготовка. `null` это «как у серии». */
  planId: string | null;
}

export type OccurrenceState = "scheduled" | "cancelled" | "moved_away";

export interface Occurrence {
  seriesId: string;
  slotId: string;
  /** Ключ занятия вместе со `slotId`. Исходная дата, а не текущее место. */
  occursOn: string;
  at: number;
  durationMin: number;
  mark: ZonedMark;
  state: OccurrenceState;
  /** Переопределение этого занятия, если оно есть. */
  override: ScheduleOccurrenceOverride | null;
  /** Куда перенесено. Заполнено у заглушки на старом месте. */
  movedTo: { date: string; time: string } | null;
  /** Откуда перенесено. Заполнено у занятия на новом месте. */
  movedFrom: { date: string; time: string } | null;
  reason: string | null;
}

/** Заведомо шире любого разумного окна: границы «бессрочно». */
const DAY_MIN = -3_000_000;
const DAY_MAX = 3_000_000;

function slotRange(
  series: ScheduleSeriesRule,
  slot: ScheduleSlotRule,
): { start: number; end: number } | null {
  const start = Math.max(
    epochDayOf(series.startsOn),
    slot.startsOn === null ? DAY_MIN : epochDayOf(slot.startsOn),
  );
  const end = Math.min(
    series.endsOn === null ? DAY_MAX : epochDayOf(series.endsOn),
    slot.endsOn === null ? DAY_MAX : epochDayOf(slot.endsOn),
  );
  return start > end ? null : { start, end };
}

/**
 * Производит ли правило занятие на эту дату.
 *
 * Отдельная функция, потому что её спрашивают трое: раскрытие, второй проход
 * (там дата исходная и в окно не попадает) и ручка входа в занятие, которой
 * иначе пришлось бы верить дате из адреса.
 */
export function slotProducesDate(
  series: ScheduleSeriesRule,
  slot: ScheduleSlotRule,
  date: string,
): boolean {
  const range = slotRange(series, slot);
  if (range === null) return false;
  const day = epochDayOf(date);
  if (day < range.start || day > range.end) return false;
  return isoWeekdayOfEpochDay(day) === slot.weekday;
}

/**
 * Что мешает занятию состояться, если мешает.
 *
 * `not_produced` это «правило такой даты не производит»: не тот день недели,
 * дата вне клипа серии или слота, либо перенос уехал за конец серии.
 */
export type OccurrenceBlock = "not_produced" | "cancelled";

export type OccurrenceResolution =
  | {
      ok: true;
      /** Момент начала. У перенесённого занятия это НОВОЕ место. */
      at: number;
      /** Уже с учётом переопределения занятия: считать её второй раз не надо. */
      durationMin: number;
      mark: ZonedMark;
      /** Откуда перенесено. `null` у занятия на своём естественном месте. */
      movedFrom: { date: string; time: string } | null;
    }
  | { ok: false; reason: OccurrenceBlock };

/**
 * Одно занятие по его ключу: производится ли оно вообще и когда именно.
 *
 * ⚠️ Спрашивают это трое, и все трое обязаны получать один ответ: второй
 * проход раскрытия, ручка входа в занятие и пересчёт `scheduled_at`. Две
 * реализации одного правила разъедутся, и тогда занятие, нарисованное с
 * рабочей кнопкой, ответит на неё отказом, либо слепок момента уедет на
 * естественное время занятия, которое давно перенесли.
 *
 * ⚠️ Перенесённое занятие ВПУСКАЕТСЯ, `moved` это не отказ. Момент и
 * длительность берутся с нового места, от них же считается окно входа.
 * Отказывает только `cancelled`.
 *
 * ⚠️ Ключ занятия при этом не меняется никогда: `occursOn` остаётся исходной
 * датой, сколько бы раз занятие ни переносили.
 */
export function resolveOccurrence(args: {
  series: ScheduleSeriesRule;
  slot: ScheduleSlotRule;
  /** ИСХОДНАЯ дата занятия, вторая половина его ключа. */
  occursOn: string;
  /** Исключение на этом ключе, если оно есть. */
  exception: ScheduleExceptionRule | null;
  /**
   * Переопределение на этом ключе, если оно есть.
   *
   * ⚠️ Необязательный аргумент намеренно: длительность из него применяется
   * ЗДЕСЬ, в единственном месте, где занятие превращается в момент и отрезок.
   * Забыть его у вызывающего означает окно входа, посчитанное по чужой
   * длительности, то есть кнопку, которая закроется не тогда, когда написано.
   */
  override?: ScheduleOccurrenceOverride | null;
}): OccurrenceResolution {
  const { series, slot, occursOn, exception } = args;

  if (!slotProducesDate(series, slot, occursOn)) return { ok: false, reason: "not_produced" };
  if (exception !== null && exception.kind === "cancelled") {
    return { ok: false, reason: "cancelled" };
  }

  const durationMin = args.override?.durationMin ?? slot.durationMin ?? series.durationMin;

  if (
    exception !== null &&
    exception.kind === "moved" &&
    exception.movedToDate !== null &&
    exception.movedToTime !== null
  ) {
    // Новое место за концом серии занятия не производит: иначе `ends_on`
    // останавливал бы обычные занятия и не трогал перенесённые, то есть
    // завершение серии не работало бы вовсе
    if (series.endsOn !== null && exception.movedToDate > series.endsOn) {
      return { ok: false, reason: "not_produced" };
    }
    const moved = localToInstant(
      exception.movedToDate,
      trimLocalTime(exception.movedToTime),
      series.timeZone,
    );
    return {
      ok: true,
      at: moved.at,
      durationMin,
      mark: moved.mark,
      movedFrom: { date: occursOn, time: trimLocalTime(slot.startsAt) },
    };
  }

  const inst = localToInstant(occursOn, slot.startsAt, series.timeZone);
  return { ok: true, at: inst.at, durationMin, mark: inst.mark, movedFrom: null };
}

export interface ExpandArgs {
  series: ScheduleSeriesRule;
  exceptions: readonly ScheduleExceptionRule[];
  /**
   * Индивидуальные правки занятий этой серии.
   *
   * Необязательны: там, где раскрытие спрашивают ради одного вопроса «занято ли
   * это время» (правило столкновений), переопределения не меняют ни одного
   * момента и читать их незачем.
   */
  overrides?: readonly ScheduleOccurrenceOverride[];
  /**
   * Окно МОМЕНТАМИ, полуинтервал `[fromAt, toAt)`.
   *
   * Именно моментами, а не датами: границы человек задаёт в СВОЕЙ зоне, а
   * серия живёт в зоне преподавателя, и складывать локальные даты двух разных
   * зон это ровно та ошибка, ради ненаписания которой существует весь файл.
   */
  fromAt: number;
  toAt: number;
  /**
   * Момент отвязки пары. Занятия позже него не выдаются: войти в них всё равно
   * нельзя (`requireActivePair`), а видимое занятие с отказом на нажатии это
   * худший вид отказа. Прошлые остаются: они были.
   */
  unlinkedAt: number | null;
}

/**
 * Раскрытие серии в занятия окна.
 *
 * Два прохода, и второй не оптимизация.
 *
 * Первый идёт по датам клипа и применяет исключения по ключу произведённой
 * даты. Второй добирает переносы, ПРИЕХАВШИЕ в окно извне: у переноса ключ это
 * исходная дата, поэтому занятие, перенесённое с 17.03 на 19.04, при просмотре
 * недели 13-19.04 первым проходом не производится вовсе. Лимит переноса 180
 * дней при потолке окна 120 делает это нормой, а не углом.
 *
 * У второго прохода три отсечки, и каждая закрывает свою дыру:
 *
 * 1. исходная дата обязана по-прежнему производиться правилом. Иначе слот,
 *    переехавший со вторников на четверги, продолжал бы рисовать перенос с
 *    несуществующего вторника как живое занятие с рабочей кнопкой
 * 2. новое место не позже конца серии. Иначе `ends_on` останавливал бы первый
 *    проход и не трогал второй, то есть завершение серии не работало бы
 * 3. новое место не позже отвязки пары. Иначе занятие рисовалось бы с кнопкой,
 *    на которую ручка входа отвечает отказом
 */
export function expandSeries(args: ExpandArgs): Occurrence[] {
  const { series, exceptions, fromAt, toAt, unlinkedAt } = args;
  const zone = series.timeZone;
  if (toAt <= fromAt) return [];

  /**
   * Границы окна в локальных датах ЗОНЫ СЕРИИ, с запасом в сутки на каждую
   * сторону. Запас нужен потому, что окно приехало моментами: занятие,
   * попадающее в окно смотрящего, в зоне серии может стоять на соседней дате.
   * Лишние даты стоят одного вычисления и отсекаются проверкой момента.
   */
  const fromDay = epochDayOf(localDateAt(fromAt - DAY_MS, zone));
  const toDay = epochDayOf(localDateAt(toAt + DAY_MS, zone));

  const byKey = new Map<string, ScheduleExceptionRule>();
  for (const e of exceptions) byKey.set(`${e.slotId} ${e.occursOn}`, e);

  /**
   * Переопределения по тому же ключу занятия. Отдельной картой, а не полями
   * исключения: вид занятия и его правка это разные измерения, и правка обязана
   * пережить снятие отмены.
   */
  const overrideByKey = new Map<string, ScheduleOccurrenceOverride>();
  for (const o of args.overrides ?? []) overrideByKey.set(`${o.slotId} ${o.occursOn}`, o);

  const out: Occurrence[] = [];

  const fits = (at: number): boolean =>
    at >= fromAt && at < toAt && (unlinkedAt === null || at <= unlinkedAt);

  for (const slot of series.slots) {
    const range = slotRange(series, slot);
    if (range === null) continue;
    const start = Math.max(range.start, fromDay);
    const end = Math.min(range.end, toDay);
    if (start > end) continue;

    const time = parseLocalTime(slot.startsAt);
    if (time === null) continue;
    const ruleDuration = slot.durationMin ?? series.durationMin;

    // Первая дата нужного дня недели не раньше начала клипа
    let day = start + ((slot.weekday - isoWeekdayOfEpochDay(start) + 7) % 7);
    for (; day <= end; day += 7) {
      const date = localDateOfEpochDay(day);
      const fields = parseLocalDate(date);
      if (fields === null) continue;
      const inst = zonedToInstant(
        fields.year,
        fields.month,
        fields.day,
        time.hour,
        time.minute,
        zone,
      );
      if (!fits(inst.at)) continue;

      const ex = byKey.get(`${slot.id} ${date}`);
      const override = overrideByKey.get(`${slot.id} ${date}`) ?? null;
      out.push({
        seriesId: series.id,
        slotId: slot.id,
        occursOn: date,
        at: inst.at,
        // Своя длительность занятия перевешивает и слот, и серию
        durationMin: override?.durationMin ?? ruleDuration,
        mark: inst.mark,
        override,
        state:
          ex === undefined ? "scheduled" : ex.kind === "cancelled" ? "cancelled" : "moved_away",
        movedTo:
          ex !== undefined && ex.kind === "moved" && ex.movedToDate !== null && ex.movedToTime !== null
            ? { date: ex.movedToDate, time: trimLocalTime(ex.movedToTime) }
            : null,
        movedFrom: null,
        reason: ex?.reason ?? null,
      });
    }
  }

  const slotById = new Map(series.slots.map((s) => [s.id, s]));

  for (const ex of exceptions) {
    if (ex.kind !== "moved") continue;
    const slot = slotById.get(ex.slotId);
    if (slot === undefined) continue;

    /**
     * Отсечки 1 и 2 (исходная дата всё ещё производится правилом, новое место
     * внутри жизни серии) считает `resolveOccurrence`, и это не вынос ради
     * краткости: ровно те же два вопроса задаёт ручка входа в занятие. Две
     * реализации одного правила однажды разъезжаются, и тогда занятие,
     * нарисованное с рабочей кнопкой, отвечает на неё отказом.
     */
    const override = overrideByKey.get(`${ex.slotId} ${ex.occursOn}`) ?? null;
    const resolved = resolveOccurrence({
      series,
      slot,
      occursOn: ex.occursOn,
      exception: ex,
      override,
    });
    if (!resolved.ok || resolved.movedFrom === null) continue;
    // Отсечка 3: новое место до отвязки пары. Она же и есть проверка окна
    if (!fits(resolved.at)) continue;

    out.push({
      seriesId: series.id,
      slotId: slot.id,
      occursOn: ex.occursOn,
      at: resolved.at,
      durationMin: resolved.durationMin,
      mark: resolved.mark,
      override,
      state: "scheduled",
      movedTo: null,
      movedFrom: resolved.movedFrom,
      reason: ex.reason,
    });
  }

  out.sort((a, b) => a.at - b.at || (a.slotId < b.slotId ? -1 : a.slotId > b.slotId ? 1 : 0));
  return out;
}

/* ────────────────────────────── потолки ──────────────────────────────────── */

/**
 * Окно расписания не длиннее 120 дней и не больше 500 занятий в ответе.
 *
 * Числа живут в общем пакете, потому что читают их оба: сервер режет ими
 * выборку, клиент называет их словами. Разъехавшись, они дали бы подпись,
 * которая врёт про длину списка. Тот же приём, что у `ARCHIVE_LIST_LIMIT`.
 */
export const SCHEDULE_WINDOW_MAX_DAYS = 120;
export const SCHEDULE_OCCURRENCE_LIMIT = 500;

/** Предел переноса в любую сторону: дальше занятие теряется из любого окна. */
export const SCHEDULE_MOVE_MAX_DAYS = 180;

/** Границы длительности занятия. Верхняя заодно держит «не больше двух суток». */
export const LESSON_DURATION_MIN = 5;
export const LESSON_DURATION_MAX = 600;

/**
 * Потолок числа слотов в серии. Не техническое ограничение, а признак
 * опечатки: четырнадцать это два занятия в день всю неделю.
 */
export const SERIES_SLOT_MAX = 14;

/* ─────────────────────── внешняя ссылка на созвон ────────────────────────── */

/**
 * Режим сосуществования: занятие живёт в нашем расписании, а созвон в Zoom,
 * Телемосте или Meet. Разбор в `docs/switching-costs.md`, раздел 7.
 *
 * ⚠️ **Ссылка не поражается в правах.** Она показывается наравне с кнопкой
 * входа в нашу комнату, не прячется в меню и не сопровождается уговорами
 * перейти к нам. Соблазн сделать её на полшага неудобнее запрещён решением
 * владельца 20.08.2026: так теряется не комната, а расписание.
 */

/** Практический предел адресной строки. Ссылка Zoom около 90 символов. */
export const EXTERNAL_CALL_URL_MAX = 2000;

function nonEmpty(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * Домен для показа РЯДОМ со ссылкой. `zoom.us`, `telemost.yandex.ru`.
 *
 * ⚠️ Показывать его обязательно, и это не украшение. Ссылку ввёл один
 * пользователь, а нажимает её другой, часто несовершеннолетний, и без домена
 * подпись «Созвон» одинаково выглядит и у Телемоста, и у чего угодно ещё.
 * Белого списка мы не держим (см. `ExternalCallUrl`), поэтому домен это
 * единственное, что мы про адрес говорим вслух.
 *
 * Три решения, и каждое в сторону честности показа:
 *
 * 1. **`host`, а не `hostname`:** порт остаётся видимым. Самохостный Jitsi на
 *    нестандартном порту это не то же самое, что он же на 443
 * 2. **Ничего не отрезаем, включая `www.`** Косметика тут не стоит риска: наш
 *    ответ на вопрос «куда ведёт ссылка» обязан совпадать с адресной строкой,
 *    которую человек увидит после нажатия
 * 3. **Не-ASCII имя остаётся в punycode** (`xn--…`), потому что так его и
 *    отдаёт `URL`. Это ровно то, что нужно: буквы кириллицы, похожие на
 *    латиницу, в имени домена похожими быть перестают
 *
 * `null` означает «адрес разобрать не вышло». Показывать в этом случае надо
 * ничего, а не саму ссылку: раз мы не смогли её прочитать, то и обещать про
 * неё нечего. Записать такой адрес нельзя (см. `ExternalCallUrl`), поэтому
 * `null` тут это либо пусто, либо строка, попавшая в базу мимо наших ручек.
 */
export function externalCallHost(url: string | null | undefined): string | null {
  const value = nonEmpty(url);
  if (value === null) return null;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }
  // Схема проверяется и здесь, хотя её же требует `ExternalCallUrl`: эта
  // функция читает то, что уже лежит в базе, а данные там старше проверок
  if (parsed.protocol !== "https:") return null;
  return parsed.host === "" ? null : parsed.host;
}

/**
 * Форма адреса созвона.
 *
 * ⚠️ Обязана совпадать по смыслу с `CHECK` в базе
 * (`external_call_url ~ '^https://[^[:space:]]{3,}$' AND length(…) <= 2000`,
 * миграция `1788393600000_external_call_url`). Разъехавшись, они дали бы
 * адрес, который проходит одну границу и не проходит другую, то есть 500 на
 * вставке вместо внятного отказа.
 *
 * Три требования, и каждое закрывает свою поломку. Это чужой адрес, введённый
 * одним пользователем и показываемый **несовершеннолетнему ученику**
 * кликабельной ссылкой:
 *
 * - только `https://`. `javascript:` и `data:` в поле, которое рисуется как
 *   `href`, это готовый XSS, а `http://` это ссылка, которую по дороге
 *   подменят
 * - ни одного пробельного символа: ими прячут настоящий адрес в конце строки
 * - длина до `EXTERNAL_CALL_URL_MAX`
 *
 * ⚠️ Сравнение строгостей, чтобы разъезд был безопасным в правильную сторону.
 * `\S` в JavaScript шире, чем POSIX-класс `[[:space:]]` в Postgres (первый
 * знает про U+00A0, U+2028 и прочие), а `.max()` считает единицы UTF-16, тогда
 * как `length()` считает символы. То есть zod здесь СТРОЖЕ базы по обоим
 * пунктам, и всё, что прошло zod, база примет заведомо. Обратный перекос дал
 * бы 500.
 *
 * Белого списка доменов НЕТ намеренно: он сломается на первом же корпоративном
 * Zoom или самохостном Jitsi школы, а обойти его тривиально. Вместо запрета
 * интерфейс обязан показывать домен рядом со ссылкой, см. `externalCallHost`.
 */
export const EXTERNAL_CALL_URL_SHAPE = /^https:\/\/\S{3,}$/;

export const ExternalCallUrl = z
  .string()
  .max(EXTERNAL_CALL_URL_MAX)
  .regex(EXTERNAL_CALL_URL_SHAPE, "ссылка на созвон должна начинаться с https:// и быть без пробелов")
  /**
   * ⚠️ Четвёртое требование, которого в базе нет: адрес обязан разбираться.
   * `https://[[[` проходит и регулярное выражение, и `CHECK`, но `URL` на нём
   * бросает, то есть домен рядом со ссылкой показать нечем. Показывать
   * кликабельный адрес, про который мы ничего не можем сказать, запрещено тем
   * же правилом, по которому нет белого списка: пусть отказ случится на вводе,
   * у того, кто адрес набрал, а не молча на экране у ребёнка.
   */
  .refine((v) => externalCallHost(v) !== null, "адрес созвона не разбирается");
export type ExternalCallUrl = z.infer<typeof ExternalCallUrl>;

/**
 * Та же ссылка НА ВЫХОДЕ, и схема тут нарочно слабее.
 *
 * ⚠️ Строгая схема на ответе это способ уронить весь ответ из-за одной строки.
 * Значение в базе старше сегодняшних проверок и переживёт их изменение, а
 * `ScheduleResponse.parse` на сервере превратил бы неразбираемый адрес одного
 * занятия в 500 на всю неделю расписания. Строгость стоит на ВВОДЕ, где её
 * цена это внятный отказ одному человеку; на выводе достаточно строки, а
 * решение «показывать или нет» принимает `externalCallHost`.
 */
export const ExternalCallUrlView = z.string();

/**
 * Разрешение ссылки, и оно единственное на весь проект:
 *
 * ```
 * lesson.external_call_url
 *   ?? (slot_id -> schedule_slot -> schedule_series.external_call_url)
 *   ?? ссылки нет
 * ```
 *
 * У разового занятия (урок со `scheduled_at` и без `slot_id`) серии нет вовсе,
 * поэтому ссылка живёт только в колонке урока. У ещё не материализованного
 * занятия серии урока нет, и ссылка берётся из серии.
 *
 * ⚠️ Пустая строка считается отсутствием ссылки наравне с `null`. В базе её не
 * бывает (`CHECK` требует не меньше трёх символов после `https://`), но
 * значение сюда приезжает и из формы, где человек стёр текст, а пустой `href`
 * ведёт на текущую страницу, то есть выглядит как сломанная кнопка.
 */
export function resolveExternalCallUrl(
  lessonUrl: string | null | undefined,
  seriesUrl: string | null | undefined,
): string | null {
  return nonEmpty(lessonUrl) ?? nonEmpty(seriesUrl) ?? null;
}

/**
 * Тело ручки «поменять ссылку у занятия».
 *
 * `null` означает «убрать», и у занятия серии это не то же самое, что «нет
 * ссылки»: убрав свою, занятие возвращается к ссылке серии. У разового занятия
 * серии нет вовсе, и `null` там означает буквально «ссылки нет».
 */
export const SetExternalCallUrlRequest = z.object({
  url: ExternalCallUrl.nullable(),
});
export type SetExternalCallUrlRequest = z.infer<typeof SetExternalCallUrlRequest>;

/* ──────────────────────────── контракт ручек ─────────────────────────────── */

export const SeriesId = z.string().uuid();
export type SeriesId = z.infer<typeof SeriesId>;

export const SlotId = z.string().uuid();
export type SlotId = z.infer<typeof SlotId>;

const DurationMin = z.number().int().min(LESSON_DURATION_MIN).max(LESSON_DURATION_MAX);

export const ScheduleSlotInput = z.object({
  weekday: Weekday,
  startsAt: LocalTime,
  /** `null` означает «как у серии», и это не то же самое, что отсутствие поля. */
  durationMin: DurationMin.nullable().optional(),
  startsOn: LocalDate.nullable().optional(),
  endsOn: LocalDate.nullable().optional(),
});
export type ScheduleSlotInput = z.infer<typeof ScheduleSlotInput>;

/**
 * Заготовка серии.
 *
 * ⚠️ Отдельная схема, потому что её читают трое: создание серии, правка серии и
 * создание занятия из диалога календаря. `null` это «без заготовки», и это не
 * то же самое, что отсутствие поля при правке.
 *
 * Одна серия это одна заготовка, и так решил владелец. Курс (упорядоченный
 * набор, откуда занятие берёт следующую непройденную) это не отменяет: он
 * приедет соседним полем, а разрешение заготовки для нового урока живёт в одном
 * месте, в ручке входа в занятие.
 */
const DefaultPlanId = z.string().uuid().nullable();

export const CreateSeriesRequest = z.object({
  studentUserId: z.string().uuid(),
  /**
   * Зона СЕРИИ, а не профиля. Копия здесь стоит ничего и делает выбор явным:
   * преподаватель, переехавший в другой город, сам решает, что делать с уже
   * согласованными занятиями, а мы не решаем за него молча.
   */
  timeZone: TimeZoneName,
  startsOn: LocalDate,
  endsOn: LocalDate.nullable().optional(),
  durationMin: DurationMin.optional(),
  lessonTitle: z.string().min(1).max(200).nullable().optional(),
  /** ⚠️ Приватная заметка преподавателя. Ученику не уезжает никогда (раздел 9). */
  note: z.string().max(500).nullable().optional(),
  defaultPlanId: DefaultPlanId.optional(),
  /**
   * Ссылка на созвон в чужом сервисе. Умолчание серии: персональная комната
   * Zoom (PMI) у преподавателя постоянная, и менять её на каждое занятие не
   * будет никто. `null` это «ссылки нет».
   */
  externalCallUrl: ExternalCallUrl.nullable().optional(),
  slots: z.array(ScheduleSlotInput).min(1).max(SERIES_SLOT_MAX),
});
export type CreateSeriesRequest = z.infer<typeof CreateSeriesRequest>;

export const UpdateSeriesRequest = z.object({
  timeZone: TimeZoneName.optional(),
  startsOn: LocalDate.optional(),
  endsOn: LocalDate.nullable().optional(),
  durationMin: DurationMin.optional(),
  lessonTitle: z.string().min(1).max(200).nullable().optional(),
  note: z.string().max(500).nullable().optional(),
  defaultPlanId: DefaultPlanId.optional(),
  /** Отсутствие поля это «не трогать», `null` это «убрать ссылку». */
  externalCallUrl: ExternalCallUrl.nullable().optional(),
});
export type UpdateSeriesRequest = z.infer<typeof UpdateSeriesRequest>;

/**
 * Правка слота идёт `UPDATE` НА МЕСТЕ, `id` не меняется никогда.
 *
 * Разбиения слота («закрыть старый, завести новый») нет и не будет: `slot_id`
 * это первая половина ключа занятия, и его смена воскрешала бы отменённые
 * занятия и осиротевала подготовленные уроки.
 *
 * `force` это подтверждение человека, увидевшего список осиротевших уроков.
 * Та же форма, что у `SyncLessonPlanRequest`.
 */
export const UpdateSlotRequest = z.object({
  weekday: Weekday.optional(),
  startsAt: LocalTime.optional(),
  durationMin: DurationMin.nullable().optional(),
  startsOn: LocalDate.nullable().optional(),
  endsOn: LocalDate.nullable().optional(),
  force: z.boolean().optional(),
});
export type UpdateSlotRequest = z.infer<typeof UpdateSlotRequest>;

export const ScheduleSlotView = z.object({
  id: SlotId,
  seriesId: SeriesId,
  weekday: Weekday,
  startsAt: LocalTime,
  durationMin: z.number().int().nullable(),
  startsOn: LocalDate.nullable(),
  endsOn: LocalDate.nullable(),
});
export type ScheduleSlotView = z.infer<typeof ScheduleSlotView>;

export const ScheduleSeriesView = z.object({
  id: SeriesId,
  teacherUserId: z.string().uuid(),
  studentUserId: z.string().uuid(),
  /** Имя второй стороны. Своё имя человек и так знает. */
  counterpartName: z.string().nullable(),
  /** Зона второй стороны: без неё нельзя договориться о времени (раздел 2.5). */
  counterpartTimeZone: TimeZoneName.nullable(),
  timeZone: TimeZoneName,
  startsOn: LocalDate,
  endsOn: LocalDate.nullable(),
  durationMin: z.number().int(),
  lessonTitle: z.string().nullable(),
  /**
   * ⚠️ Отсутствует в ответе ученику ВСЕГДА. Разница с `reason` исключения не
   * косметическая: в такие поля преподаватели пишут то, чего ученик видеть не
   * должен. Правило стоит в одном месте сборки ответа, как `planTitle`.
   */
  note: z.string().nullable().optional(),
  /**
   * Заготовка серии. ⚠️ Отсутствует в ответе ученику ВСЕГДА, тем же правилом и
   * в том же месте, что `note`: имя чужого материала ученику не адресуемо, и
   * ручки заготовок отвечают ему 404.
   */
  defaultPlanId: z.string().uuid().nullable().optional(),
  /** Название заготовки, чтобы экран серии не ходил за ним вторым запросом. */
  defaultPlanTitle: z.string().nullable().optional(),
  /**
   * Серия входит в ПУЧОК пар одной группы (раздел 3
   * `docs/group-lessons-design.md`). `null` это обычная серия одной пары.
   *
   * ⚠️ **Отсутствует в ответе ученику ВСЕГДА**, тем же правилом и в том же
   * месте, что `note` и `defaultPlanId`, но довод другой и он сильнее.
   * «Ученик не отличает групповое занятие от индивидуального»: в его
   * расписании это его занятие, и признаков группы там нет ни одного, пока
   * состав группы не раскрыт преподавателем. А раскрытый состав ученик видит
   * не здесь, а в карточке сообщения (раздел 8 проекта), поэтому ветки «а если
   * `membersVisible`» тут нет вовсе: одно правило без исключений не забывают.
   *
   * ⚠️ Тип это `z.string().uuid()`, а не импортированный `GroupId`, и это то же
   * решение, что у словаря `attendance` ниже: `./groups.ts` и этот файл не
   * должны знать друг о друге, а цикл модулей в ESM это не предупреждение, а
   * `ReferenceError` на инициализации схемы zod в мёртвой зоне.
   */
  groupId: z.string().uuid().nullable().optional(),
  /** Название группы. ⚠️ Отсутствует в ответе ученику ВСЕГДА, см. выше. */
  groupTitle: z.string().nullable().optional(),
  myRole: z.enum(["teacher", "student"]),
  /** Пара отвязана: прошлое видно, новых занятий не будет. */
  unlinked: z.boolean(),
  /**
   * Ссылка на созвон в чужом сервисе.
   *
   * ⚠️ Уезжает ОБЕИМ сторонам, в отличие от `note` и `defaultPlanId`, и это не
   * недосмотр, а суть режима сосуществования: ученик обязан видеть, куда
   * заходить на занятие. Раздел 7 `docs/switching-costs.md`.
   */
  externalCallUrl: ExternalCallUrlView.nullable(),
  slots: z.array(ScheduleSlotView),
});
export type ScheduleSeriesView = z.infer<typeof ScheduleSeriesView>;

export const ScheduleSeriesListResponse = z.object({
  series: z.array(ScheduleSeriesView),
});
export type ScheduleSeriesListResponse = z.infer<typeof ScheduleSeriesListResponse>;

/* ────────────────────────── отмена и перенос ─────────────────────────────── */

/**
 * Отмена занятия.
 *
 * `reason` видна ОБЕИМ сторонам, в отличие от `note` серии: под полем ввода так
 * и написано, «причину увидит ученик». Отменённое занятие остаётся в сетке
 * зачёркнутым, потому что исчезнувшее занятие читается как сбой продукта, и
 * человек идёт писать в поддержку вместо того, чтобы понять, что его отменили.
 *
 * `force` это подтверждение человека, увидевшего, что вместе с занятием в
 * корзину уедет подготовленный урок. Та же форма, что у `lessons_orphaned`.
 */
export const CancelOccurrenceRequest = z.object({
  reason: z.string().max(300).nullable().optional(),
  force: z.boolean().optional(),
});
export type CancelOccurrenceRequest = z.infer<typeof CancelOccurrenceRequest>;

/**
 * Перенос занятия.
 *
 * ⚠️ Адресуется занятие своим ключом, то есть ИСХОДНОЙ датой, сколько бы раз
 * его ни переносили. Повторный перенос это правка той же строки, а не вторая:
 * цепочки «перенесено с перенесённого» не возникает, и в интерфейсе всегда одна
 * пара «откуда и куда».
 *
 * Перенос на дату раньше исходной разрешён: «давайте на день раньше» это
 * обычная просьба. Ограничение только по модулю, `SCHEDULE_MOVE_MAX_DAYS`.
 */
export const MoveOccurrenceRequest = z.object({
  toDate: LocalDate,
  toTime: LocalTime,
  reason: z.string().max(300).nullable().optional(),
});
export type MoveOccurrenceRequest = z.infer<typeof MoveOccurrenceRequest>;

export const ScheduleExceptionView = z.object({
  slotId: SlotId,
  /** ИСХОДНАЯ дата занятия. При переносе не меняется никогда. */
  occursOn: LocalDate,
  kind: z.enum(["cancelled", "moved"]),
  movedToDate: LocalDate.nullable(),
  movedToTime: LocalTime.nullable(),
  reason: z.string().nullable(),
});
export type ScheduleExceptionView = z.infer<typeof ScheduleExceptionView>;

/* ─────────────────── индивидуальная правка занятия серии ─────────────────── */

/**
 * Правка ОДНОГО занятия серии: своя заготовка, своё название, своя
 * длительность.
 *
 * ⚠️ Все три поля трёхзначны, и это не педантизм. Отсутствие поля означает «не
 * трогать», а `null` означает «убрать переопределение, вернуть как у серии».
 * Свалив их в одно, мы получили бы форму, в которой нельзя поменять название,
 * не потеряв заготовку.
 *
 * ⚠️ Занятие адресуется своим ключом `(slotId, occursOn)`, то есть ИСХОДНОЙ
 * датой, сколько бы раз его ни переносили. Тот же адрес, что у отмены и
 * переноса, и это единственный адрес занятия во всём проекте.
 */
export const OccurrenceOverrideRequest = z.object({
  title: z.string().min(1).max(200).nullable().optional(),
  durationMin: DurationMin.nullable().optional(),
  planId: z.string().uuid().nullable().optional(),
});
export type OccurrenceOverrideRequest = z.infer<typeof OccurrenceOverrideRequest>;

/**
 * Переопределение наружу.
 *
 * ⚠️ Своя схема, а не `ScheduleExceptionView`: там `kind` объявлен `z.enum`, и
 * дописать в него третье значение значило бы уронить разбор ВСЕГО ответа у
 * вкладки со старым бандлом. Здесь новое поле в новой схеме, которую старый
 * бандл не читает вовсе.
 *
 * ⚠️ Уезжает ТОЛЬКО преподавателю: `planTitle` это имя чужого материала, а
 * ручки заготовок отвечают ученику 404. Правило стоит в одном месте сборки
 * ответа, как `note` серии.
 */
export const OccurrenceOverrideView = z.object({
  slotId: SlotId,
  occursOn: LocalDate,
  title: z.string().nullable(),
  durationMin: z.number().int().nullable(),
  planId: z.string().uuid().nullable(),
  /** Название заготовки, чтобы карточка занятия не ходила за ним вторым запросом. */
  planTitle: z.string().nullable(),
});
export type OccurrenceOverrideView = z.infer<typeof OccurrenceOverrideView>;

/* ──────────────────────────── разовое занятие ────────────────────────────── */

/**
 * Разовое занятие: «давайте дополнительно в субботу».
 *
 * ⚠️ Момент считает СЕРВЕР из даты, времени и зоны, а не клиент числом. Это то
 * же правило 4.1 проекта, по которому раскрытие живёт на сервере: ICU в
 * браузере это другая база часовых поясов, и слепок, посчитанный там, разошёлся
 * бы с занятиями серий на зоне, чьи правила поменялись между релизами.
 *
 * ⚠️ Зона приезжает своим полем, а не берётся из профиля, потому что человек
 * набирает дату и время глядя на календарь, а календарь нарисован в зоне
 * смотрящего. Взяв зону из профиля при неподтверждённом поясе, мы поставили бы
 * занятие не туда, куда человек показал мышью.
 */
export const CreateSingleLessonRequest = z.object({
  studentUserId: z.string().uuid(),
  date: LocalDate,
  time: LocalTime,
  timeZone: TimeZoneName,
  durationMin: DurationMin,
  /** Пусто означает «Занятие <дата прописью>»: `lesson.title` объявлен NOT NULL. */
  title: z.string().min(1).max(200).nullable().optional(),
  planId: z.string().uuid().nullable().optional(),
  /**
   * Ссылка на созвон в чужом сервисе, СВОЯ у этого занятия.
   *
   * ⚠️ У разового занятия серии нет вовсе, и унаследовать ссылку ему неоткуда:
   * это единственное место, где она может у него появиться при заведении.
   * Без этого поля преподаватель, ведущий в Zoom, заводил бы занятие и тут же
   * шёл править его второй ручкой, а до тех пор занятие стояло бы в расписании
   * без адреса, по которому на него приходят.
   */
  externalCallUrl: ExternalCallUrl.nullable().optional(),
});
export type CreateSingleLessonRequest = z.infer<typeof CreateSingleLessonRequest>;

/**
 * Перенос разового занятия.
 *
 * Своей ручкой, а не переносом занятия: у занятия серии перенос это ОДНА строка
 * исключения, знающая и «откуда», и «куда», а у разового переезжает сам урок.
 * Второй путь к тому же сдвигу разъехался бы с первым, поэтому урок со слотом
 * эта ручка отвергает.
 */
export const UpdateSingleLessonRequest = z.object({
  date: LocalDate,
  time: LocalTime,
  timeZone: TimeZoneName,
  durationMin: DurationMin.optional(),
});
export type UpdateSingleLessonRequest = z.infer<typeof UpdateSingleLessonRequest>;

/* ─────────────────────────── сетка расписания ────────────────────────────── */

/**
 * Окно входа в занятие.
 *
 * Пятнадцать минут форы преподавателю это не привилегия, а следствие того, что
 * заготовка урока берётся из серии только при создании: если урок заведёт
 * ученик, `plan_id` останется пустым навсегда. Если преподаватель опоздал,
 * ученик всё равно войдёт: правило «не срываем занятие» весит больше правила
 * «урок должен быть аккуратным».
 *
 * Пять минут ученику это решение владельца от 15.08.2026. До него нижней
 * границей стояло начало ровно, и человек, пришедший за минуту, упирался в
 * отказ на глазах у ждущего преподавателя. Пять минут это ровно тот запас, за
 * который принято приходить, и он не съедает форы преподавателя.
 *
 * Три часа после конца это грубая величина НАМЕРЕННО: жёсткое окно входа по
 * часам ровно тот класс отказов, ради отсутствия которого написан весь проект.
 */
export const ENTRY_OPENS_TEACHER_MIN = 15;
export const ENTRY_OPENS_STUDENT_MIN = 5;
export const ENTRY_CLOSES_AFTER_MIN = 180;

/**
 * Насколько свежим должен быть `lesson_participant.last_seen_at`
 * преподавателя, чтобы считать его сидящим в комнате прямо сейчас.
 *
 * ⚠️ Число не берётся с потолка и не может быть меньше, чем интервал
 * перезапроса тикетов доски (`COLLAB_TOKEN_SYNC_INTERVAL_MS`, сейчас 10 минут).
 * Журнал присутствия трогают ровно два места: выдача токена LiveKit и выдача
 * тикета доски, а тикет на живом соединении перезапрашивается по этому таймеру.
 * Значит преподаватель, молча рисующий на доске, обновляет свою отметку раз в
 * десять минут, и окно короче десяти минут выкидывало бы его из комнаты в
 * глазах сервера, пока он в ней сидит.
 *
 * Цена запаса названа честно: преподаватель, закрывший вкладку, ещё около
 * четверти часа считается присутствующим, и всё это время ученик может войти
 * раньше своих пяти минут. Это ровно та ошибка, которую продукт выбирает
 * делать: пустить лишний раз дешевле, чем сорвать занятие.
 */
export const TEACHER_PRESENT_WITHIN_MIN = 15;

export interface EntryWindow {
  opensAt: number;
  closesAt: number;
}

/**
 * Границы окна входа для роли.
 *
 * ⚠️ Одна функция на двоих: по ней сетка расписания рисует состояние занятия, и
 * по ней же ручка входа решает, впускать ли. Разъехавшись, они дали бы кнопку
 * «Войти», отвечающую `409 entry_closed`, то есть ровно тот класс отказов, ради
 * отсутствия которого написан весь проект.
 *
 * ⚠️ Считается от `at` РАСКРЫТИЯ, а не от `lesson.scheduled_at`. Слепок снят при
 * создании урока, а «подготовить» жмут задолго до занятия: между слепком и
 * занятием момент уезжает переносом, правкой слота, сменой зоны серии или
 * обновлением tzdata. Окно, посчитанное от слепка, открылось бы на час мимо.
 *
 * ⚠️ Нижняя граница ученика тут БЕЗ поправки на присутствие преподавателя:
 * присутствие это факт из базы, а функция обязана остаться чистой. Складывает
 * их `studentEntryOpen`, и звать надо именно её.
 */
export function entryWindowOf(args: {
  at: number;
  durationMin: number;
  role: "teacher" | "student";
}): EntryWindow {
  const opensBefore =
    args.role === "teacher" ? ENTRY_OPENS_TEACHER_MIN : ENTRY_OPENS_STUDENT_MIN;
  return {
    opensAt: args.at - opensBefore * 60_000,
    closesAt: args.at + (args.durationMin + ENTRY_CLOSES_AFTER_MIN) * 60_000,
  };
}

/**
 * Открыт ли вход ученику снизу.
 *
 * Правило владельца целиком: **за пять минут до начала ИЛИ если преподаватель
 * уже в комнате**. Второе слагаемое обязательно, и без него жёсткое правило
 * срывает занятие: преподаватель решил начать раньше, говорит «заходи», а
 * ученик упирается в отказ и ничего с этим сделать не может.
 *
 * ⚠️ Верхнюю границу эта функция не знает вовсе и знать не должна. Она одна на
 * два разных места (ручка входа в занятие и проверка доступа к уроку), а
 * верхняя граница считается от длительности, которой у второго из них нет.
 */
export function studentEntryOpen(args: {
  at: number;
  now: number;
  teacherPresent: boolean;
}): boolean {
  if (args.teacherPresent) return true;
  return args.now >= args.at - ENTRY_OPENS_STUDENT_MIN * 60_000;
}

/**
 * Через сколько минут без свежего снимка занятие считается брошенным.
 *
 * ⚠️ Число обязано совпадать с интервалом в предикате архива
 * (`listArchiveLessons`, `now() - interval '15 minutes'`). Оба висят на одном
 * факте: снимок доски снимается раз в пять минут, поэтому три пропуска подряд
 * означают, что в комнате никого нет. Разъехавшись, они дадут занятие,
 * показанное в расписании завершённым, но в архиве отсутствующее.
 *
 * Из-за этого же числа состояние `done` НЕ определяется как «ученик заходил и
 * снимок есть»: периодический снимок снимается и у идущего урока, поэтому по
 * такому определению кнопка «Архив» появлялась бы на шестой минуте занятия.
 */
export const LESSON_ABANDONED_AFTER_MIN = 15;

/**
 * Брошено ли занятие.
 *
 * ⚠️ **Раньше планового конца занятие брошенным не считается НИКОГДА, и это
 * решение владельца 09.09.2026, купленное настоящим сорванным уроком.** Было
 * так: преподаватель открыл комнату за девятнадцать минут до начала (дверь ему
 * открывается за пятнадцать, а подготовленный урок пускает и раньше),
 * посмотрел и вышел. Через пятнадцать минут после последнего снимка правило
 * решило, что урок брошен, и это случилось в 16:02 у занятия, назначенного на
 * 16:00. К своему собственному началу занятие было «прошедшим», двери не
 * осталось, а на её месте стоял «Разбор».
 *
 * Черствость снимка отвечает на вопрос «в комнате давно никого», и на него она
 * отвечает верно. Но «никого нет» и «занятие кончилось» это разные вещи ровно
 * до планового конца: до него пустая комната означает перерыв, обрыв связи или
 * подготовку, а после него ничего другого уже не означает.
 *
 * ⚠️ Верхнюю границу правило не трогает: после планового конца всё работает как
 * работало, брошенное занятие приезжает в архив через четверть часа.
 */
export function lessonAbandoned(args: {
  now: number;
  /** Момент последнего снимка доски. `null` это «снимка нет вовсе». */
  snapshotTakenAt: number | null;
  /** Плановый конец занятия. */
  endsAt: number;
}): boolean {
  if (args.snapshotTakenAt === null) return false;
  if (args.now < args.endsAt) return false;
  return args.now - args.snapshotTakenAt >= LESSON_ABANDONED_AFTER_MIN * 60_000;
}

/**
 * Состояние занятия. Считает его СЕРВЕР и отдаёт готовым: часы на устройстве
 * бывают сбиты на десятки минут, и ученик с убежавшими часами получил бы
 * мёртвую кнопку без объяснений.
 *
 * ⚠️ `live`, `done` и `missed` делит один предикат «ученик заходил», то есть
 * участник с ролью `student` в `lesson_participant`, а НЕ `lesson.status`.
 * Причина в коде: `markLive` зовётся на каждой выдаче тикета доски, поэтому
 * преподаватель, разложивший материал за неделю, оставляет урок в `live`
 * навсегда. Состояние по статусу врало бы именно на подготовленном уроке, то
 * есть ровно там, ради чего оно считается.
 */
export const OccurrenceLifecycle = z.enum([
  /** Раньше окна входа. */
  "scheduled",
  /** Окно входа открыто, урока ещё нет. */
  "open",
  /** Урок есть, ученик не заходил, окно не закрылось. */
  "ready",
  /** Ученик заходил, занятие не закончилось. */
  "live",
  /** Урок завершён либо заброшен со снятым снимком. */
  "done",
  /** Стоит исключение `cancelled`. */
  "cancelled",
  /** Стоит исключение `moved`, и это СТАРОЕ место занятия. */
  "moved_away",
  /** Окно входа закрылось, ученик не заходил. */
  "missed",
  /**
   * Время занятия прошло, а нашей комнаты у него нет вовсе (`roomAvailable`
   * ложно): это занятие ГРУППЫ у преподавателя вне списка комнаты.
   *
   * ⚠️ **Заведено потому, что `missed` на таком занятии это ложь о факте**
   * (правка по итогам ревью 29.08.2026). `missed` означает «вход был открыт, и
   * никто не вошёл»; у занятия без нашей комнаты входа не было, состояться у
   * нас оно не могло по построению, а шло оно в Zoom, и что там было, мы не
   * знаем. Читает это не только преподаватель (которому в адрес мы не считаем
   * ничего, 5.10 CLAUDE.md), но и ученик с родителем.
   *
   * ⚠️ **У преподавателя ИЗ списка занятие группы это состояние не получает
   * вовсе**: комната у него есть, и работают обычные состояния. Чтобы `missed`
   * не достался при этом одному не пришедшему ребёнку на занятии, где были
   * остальные, «состоялось» считается по ВСЕЙ комнате (`room_joined` в
   * `apps/api/src/repo/schedule.ts`).
   *
   * Уйдёт вместе с `roomAvailable`, когда список снимут для всех.
   */
  "past",
]);
export type OccurrenceLifecycle = z.infer<typeof OccurrenceLifecycle>;

/**
 * Предупреждение о наложении.
 *
 * ⚠️ Уезжает ТОЛЬКО преподавателю, и правило стоит в одном месте сборки
 * ответа. Оно несёт имя второй стороны чужого занятия и его время, то есть
 * ученику это ровно то, что запрещает раздел 7 проекта: утечка не через
 * пустоту, а через прилежность.
 *
 * Кодов о доступном времени (`teacher_off_hours`, `student_off_hours`) здесь
 * пока нет: окна доступности это отдельная фаза, и таблица ещё пустая.
 */
export const ScheduleWarning = z.object({
  code: z.enum(["teacher_overlap", "student_overlap"]),
  /** Имя второй стороны того занятия, с которым наложение. */
  withName: z.string().nullable(),
  withAt: z.number(),
});
export type ScheduleWarning = z.infer<typeof ScheduleWarning>;

/**
 * Групповое занятие в строке сетки: чьё оно и сколько человек его посещает.
 *
 * ⚠️ **Поля нет в ответе ученику вовсе**, как у `override` и `warnings`, и
 * довод сильнее обычного: «ученик не отличает групповое занятие от
 * индивидуального» (раздел 3 `docs/group-lessons-design.md`). В его расписании
 * это его собственное занятие с его собственным ключом, и ни названия группы,
 * ни числа одноклассников там нет.
 *
 * ⚠️ `size` это число пар, которые ДЕЙСТВИТЕЛЬНО посещают ЭТО занятие, а не
 * размер группы. Догнавший в середине четверти в прошлые занятия не входит,
 * ушедший из будущих выпадает, у отвязанной пары занятия обрываются отвязкой,
 * и все три случая обязаны быть видны числом на карточке, а не только в
 * составе группы.
 *
 * ⚠️ `id` объявлен `z.string().uuid()`, а не импортированным `GroupId`, чтобы
 * не замыкать цикл модулей с `./groups.ts`. Та же причина, что у словаря
 * `attendance` ниже.
 */
export const ScheduleEntryGroup = z.object({
  id: z.string().uuid(),
  title: z.string(),
  size: z.number().int().positive(),
  /**
   * Имена участников раскрыты друг другу.
   *
   * ⚠️ **В `roomAvailable` это НЕ входит и входить не должно** (раздел 5
   * проекта): комната у занятия есть, но вход в неё отвечает
   * `group_members_hidden`, пока имена скрыты. Признак едет отдельным полем
   * затем, чтобы экран сказал об этом словами РЯДОМ с действием, а не отнял
   * действие: отняв, мы сделали бы отказ недостижимым из интерфейса, и тест на
   * него охранял бы мёртвый путь.
   *
   * ⚠️ **Необязательное, как `ScheduleEntry.kind` и `Lesson.kind` из той же
   * пачки.** Обязательное поле здесь роняет разбор ВСЕГО `ScheduleResponse`, то
   * есть сетка не открывается ни у одного преподавателя с группой, стоит новому
   * бандлу встретить API без этого поля. Сегодня такая встреча недостижима,
   * потому что выкатка заливает статику ПОСЛЕ перезапуска API, но запас
   * прочности стоит одну строку. Читатель к отсутствию готов: `membersHiddenNote`
   * принимает `boolean | undefined` и молчит, пока сервер не сказал `false`.
   */
  membersVisible: z.boolean().optional(),
});
export type ScheduleEntryGroup = z.infer<typeof ScheduleEntryGroup>;

/**
 * Месяц занятия и его оплата. Только у ПОМЕСЯЧНОЙ пары.
 *
 * Заведено 03.09.2026 затем, чтобы отметить оплату помесячной паре можно было
 * прямо из расписания, как поурочной. До этого дня та же работа стоила вдвое
 * дороже: у помесячной пары в сетке не было ни одного поля про деньги, и
 * отметка делалась переходом в карточку ученика.
 *
 * ⚠️ **Это НЕ `paid` соседней строкой, и путать их нельзя ни в одну сторону.**
 * `paid` говорит про ЭТО занятие, `month.paid` про весь месяц, к которому оно
 * относится: нажатие на чип месяца отмечает месяц целиком, а не занятие под
 * ним. Единицы разные, складывать их нельзя, и вместе они не приезжают никогда:
 * у пары режим либо `per_lesson` (тогда есть `paid`), либо `monthly` (тогда
 * есть `month`), либо `none` (тогда нет ни того, ни другого). Разными формами
 * (булево и объект) они сделаны в том числе поэтому: перепутанное поле это
 * ошибка компиляции, а не тихо неверный чип.
 *
 * ⚠️ Отметка месяца живёт в своей таблице и НЕ выводится из занятий месяца
 * (раздел 11.1 `docs/student-marks-design.md`): платят вперёд, и сентябрь
 * отмечают двадцать пятого августа, когда сентябрьского расписания ещё нет.
 * Поэтому `paid: false` означает «месяц не отмечен», а не «в месяце есть
 * неоплаченные занятия», и слова про просрочку тут нет и не будет: срока мы не
 * знаем.
 */
export const ScheduleEntryMonth = z.object({
  /**
   * Месяц занятия, `ГГГГ-ММ`. Ровно та строка, которую принимает
   * `PUT /v1/students/:userId/months`.
   *
   * ⚠️ Выражение продублировано из `PERIOD_RE` (`./student-profile.ts`)
   * НАМЕРЕННО, ровно как литералы `attendance` ниже и по той же причине:
   * прямой импорт замкнул бы цикл модулей (`student-profile.ts` берёт отсюда
   * `LocalDate`), а цикл в ESM это `ReferenceError` на инициализации схемы zod
   * в мёртвой зоне. Расхождение двух написаний ловит тест «месяц в сетке
   * принимает ровно то же, что PaymentPeriod».
   */
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "месяц в формате ГГГГ-ММ"),
  /** Месяц отмечен оплаченным. Нажатие на чип переключает это значение. */
  paid: z.boolean(),
});
export type ScheduleEntryMonth = z.infer<typeof ScheduleEntryMonth>;

/**
 * Строка сетки.
 *
 * ⚠️ Строк ДВА вида, и второй не оптимизация. `source: "series"` это занятие,
 * произведённое правилом, с приклеенным уроком, если он есть. `source:
 * "lesson"` это урок с заполненным `scheduled_at`, которому занятия не
 * нашлось.
 *
 * Второй вид нужен потому, что правка слота отсекает прошлое сдвигом
 * `starts_on`: после переезда вторников на четверги мартовские занятия из
 * правила больше не производятся, но мартовские УРОКИ были и обязаны остаться
 * видимыми на своих местах. Иначе человек, поправивший расписание в августе,
 * потеряет из сетки всю свою историю до августа. Сюда же попадает
 * осиротевший подготовленный урок: молча прятать его нельзя, в нём может
 * лежать разложенный материал.
 */
export const ScheduleEntry = z.object({
  /** Ключ строки: `(slotId, occursOn)` у занятия, id урока у второго вида. */
  key: z.string(),
  source: z.enum(["series", "lesson"]),
  /**
   * РАЗОВОЕ занятие: урок со `scheduled_at` и без `slot_id`.
   *
   * ⚠️ Отдельным булевым полем, а не третьим значением `source`, и это не
   * стилистика: `source` объявлен `z.enum`, и неизвестное значение уронило бы
   * разбор ВСЕГО ответа у вкладки со старым бандлом. Лишнее булево поле такой
   * разбор просто отбрасывает.
   *
   * Различать эти два вида второго потока обязательно. У осиротевшего урока
   * (`slotId` есть, занятия ему не нашлось) состояния нет вовсе и подпись
   * «занятие снято с расписания» верна. У разового занятия занятие никто не
   * снимал, его так и завели: у него обычные состояния и обычные кнопки.
   */
  standalone: z.boolean(),
  /** Момент начала, миллисекунды эпохи. Показывать его в своей зоне дело клиента. */
  at: z.number(),
  /** `null` бывает только у второго вида, когда слот уже не найти. */
  durationMin: z.number().int().nullable(),
  seriesId: SeriesId.nullable(),
  /**
   * Зона СЕРИИ, а не смотрящего.
   *
   * Едет отдельным полем потому, что перенос задаётся локальными датой и
   * временем серии, а видит человек своё время. Без этого поля экран переноса
   * либо спрашивал бы время в чужой зоне молча, либо приходилось бы дочитывать
   * серию вторым запросом ради одной строки.
   */
  seriesTimeZone: TimeZoneName.nullable(),
  slotId: SlotId.nullable(),
  /** ИСХОДНАЯ дата занятия, вторая половина его ключа. */
  occursOn: LocalDate.nullable(),
  /** `null` у второго вида: у него нет состояния, есть только сам урок. */
  state: OccurrenceLifecycle.nullable(),
  /** Особое локальное время: несуществующий час весной, двойной осенью. */
  mark: z.enum(["none", "gap", "ambiguous"]),
  title: z.string().nullable(),
  counterpartName: z.string().nullable(),
  counterpartUserId: z.string().uuid().nullable(),
  myRole: z.enum(["teacher", "student"]),
  lessonId: z.string().uuid().nullable(),
  /** Куда перенесено. Заполнено на СТАРОМ месте занятия. */
  movedTo: z.object({ date: LocalDate, time: LocalTime }).nullable(),
  /** Откуда перенесено. Заполнено на НОВОМ месте занятия. */
  movedFrom: z.object({ date: LocalDate, time: LocalTime }).nullable(),
  /** Причина отмены или переноса. Видна обеим сторонам, в отличие от `note`. */
  reason: z.string().nullable(),
  /**
   * Локальное время этого же занятия неделю назад, если оно было другим.
   *
   * Это и есть пометка о переводе часов: серия стоит на месте, а часы у одной
   * из сторон перевели, и у неё занятие уехало. Молчать тут нельзя, молчание
   * означает пропущенное занятие.
   */
  shiftedFromTime: LocalTime.nullable(),
  /**
   * У урока есть снимок доски и он уже не идёт, то есть его можно открыть
   * разбором.
   *
   * Отдельное поле, а не вывод из состояния: `done` бывает и у урока без
   * снимка (кнопку «завершить» нажали на пустой доске), и тогда разбор
   * показал бы экран «архив готовится», который не закончится никогда. Условие
   * здесь ровно то же, что в предикате списка архива.
   */
  archivable: z.boolean(),
  /**
   * У занятия своя заготовка, своё название или своя длительность.
   *
   * ⚠️ Отдельным булевым полем, а не значением в `state` или в `source`: оба
   * объявлены `z.enum`, и неизвестное значение уронило бы разбор ВСЕГО ответа у
   * вкладки со старым бандлом. Лишнее булево поле такой разбор просто
   * отбрасывает. Тем же приёмом заведён `standalone`.
   *
   * Поле едет ОБЕИМ сторонам, в отличие от `override` ниже: ученику полезно
   * знать, что в этот четверг занятие не такое, как обычно, а вот из чего оно
   * состоит (какая заготовка) ему не адресуемо.
   */
  customized: z.boolean(),
  /**
   * Само переопределение, чтобы форма правки открывалась заполненной.
   *
   * ⚠️ Поля НЕТ в ответе ученику вовсе, как и у `warnings`: `planTitle` это имя
   * чужого материала. Не пустой объект, а отсутствие поля, чтобы «нет правки» и
   * «не положено» не путались ни в коде, ни в JSON.
   */
  override: OccurrenceOverrideView.optional(),
  /** ⚠️ Поля НЕТ в ответе ученику вовсе. */
  warnings: z.array(ScheduleWarning).optional(),
  /**
   * Ссылка на созвон, УЖЕ РАЗРЕШЁННАЯ сервером по правилу
   * `resolveExternalCallUrl`. Клиент её не выводит: два места, считающие одно
   * и то же, однажды разойдутся, и увидит это пользователь.
   *
   * ⚠️ Едет ОБЕИМ сторонам и показывается наравне с кнопкой входа в нашу
   * комнату. Соблазн спрятать её поглубже, чтобы подтолкнуть к нам, запрещён
   * решением владельца 20.08.2026 (раздел 7 `docs/switching-costs.md`): так
   * теряется не комната, а расписание.
   */
  externalCallUrl: ExternalCallUrlView.nullable(),
  /**
   * Ссылка принадлежит самому занятию, а не унаследована от серии.
   *
   * Нужно ровно для формы правки: без этого признака поле пришлось бы
   * заполнять разрешённой ссылкой, и первое же сохранение превращало бы
   * наследование в копию, после чего правка серии перестала бы доезжать до
   * занятия молча.
   */
  externalCallOwn: z.boolean(),
  /**
   * У занятия есть НАША комната: доска, упражнения, видео.
   *
   * ⚠️ Отдельным полем, а не выводом из состояния, и заведено оно ради одного
   * случая: **у группового занятия комната есть не всегда**. С 01.09.2026 она
   * есть у преподавателя с непустой датой включения (`app_user.group_room_since`,
   * условия 6 и 9 рубежа включения в разделе 17 `docs/group-room-design.md`) и
   * только у занятий не раньше этой даты: то, что месяцами шло в Zoom по
   * внешней ссылке, комнаты не получает. Остальным ручка входа отвечает отказом
   * `group_room_unavailable`. Кнопка, ведущая в отказ, хуже отсутствующей (тот
   * же довод, что у поурочной отметки в `marksOf`), поэтому признак обязан
   * доехать до клиента, а не выводиться им.
   *
   * ⚠️ **`members_visible` в это поле НЕ входит** (раздел 5 проекта): иначе
   * отказ `group_members_hidden` стал бы недостижим из интерфейса, а текст про
   * закрытый состав рисуется рядом с действием, а не отнимает действие.
   *
   * ⚠️ **Едет ОБЕИМ сторонам, и ложное значение это точный признак группы.**
   * Раньше здесь стояло обратное («признаком группы не является»), и это была
   * неправда: ложным поле становится ровно у занятия группы, поэтому ученик по
   * нему безошибочно отличает групповое от индивидуального. Утечка принята
   * осознанно и она узкая: ученик узнаёт «занятие идёт без нашей комнаты», но
   * ни названия группы, ни её состава, ни числа участников (поле `group` в его
   * ответе отсутствует вовсе). ⚠️ Заметить стоит и обратную сторону: у
   * преподавателя ИЗ списка поле истинно у обоих видов занятия, то есть утечка
   * закрывается сама, по мере включения фичи.
   *
   * Когда список снимут для всех, поле станет истинным везде, и убрать его
   * можно будет одним коммитом вместе с отказом.
   */
  roomAvailable: z.boolean(),
  /**
   * Личное занятие или групповое.
   *
   * ⚠️ **Отдельным полем, а не выводом из наличия `group`**, и разница не
   * стилистическая. Поле `group` несёт только СВЁРНУТАЯ строка занятия серии, а
   * строка второго потока его не несёт никогда: осиротевший урок занятия группы
   * (расписание поправили после того, как оно прошло) приезжает без неё. Пока
   * вид читался по пустоте, такое занятие получало фигуру одного человека, то
   * есть экран говорил про него неправду.
   *
   * ⚠️ **Поля НЕТ в ответе ученику вовсе**, как у `group` и `warnings`: раздел 3
   * `docs/group-lessons-design.md` запрещает ему отличать групповое занятие от
   * индивидуального. Отсутствие означает «мы этого не сказали», а не «занятие
   * личное», и на этом различии стоит проверка роли в `EntryKind.tsx`.
   */
  kind: z.enum(["student", "group"]).optional(),
  /** ⚠️ Поля НЕТ в ответе ученику вовсе, см. `ScheduleEntryGroup`. */
  group: ScheduleEntryGroup.optional(),
  /**
   * Заготовка ЭТОГО занятия, уже разрешённая сервером.
   *
   * Заведено 03.09.2026 ради кнопки «Задать домашнюю» в карточке занятия: форма
   * подставляет заготовку, а взять её было неоткуда. В строке лежала только
   * правка занятия (`override.planId`), заготовки серии не лежало вовсе, и
   * экран доспрашивал её отдельным `GET /v1/lessons/:id` на каждое открытие
   * карточки. Это лишний круг по сети на самой горячей поверхности кабинета и
   * на канале 128 кбит/с (раздел 3.1 CLAUDE.md), тогда как на сервере это ноль
   * запросов: обе колонки уже едут в выборках сетки.
   *
   * ⚠️ **Значение РАЗРЕШЁННОЕ, а не сырое**, ровно как `externalCallUrl`
   * рядом. Правило («урок, иначе правка занятия, иначе заготовка серии») живёт
   * на сервере в одном месте, и клиент его не повторяет: два места, считающие
   * одно и то же, однажды разойдутся. Разбор порядка в `planOf`
   * (`apps/api/src/schedule-grid.ts`).
   *
   * ⚠️ **Поля НЕТ в ответе ученику вовсе**, тем же правилом, что у `override` и
   * `ScheduleSeriesView.defaultPlanId`: имя чужого материала ученику не
   * адресуемо, а ручки заготовок отвечают ему 404. Нет его и у сводной строки
   * занятия группы: домашка принадлежит ученику, а сводная строка не несёт ни
   * одного.
   *
   * ⚠️ `null` и отсутствие ключа это РАЗНЫЕ ответы: `null` означает «заготовки
   * у занятия нет», отсутствие означает «мы этого не сказали».
   */
  planId: z.string().uuid().nullable().optional(),
  /**
   * Отмечена ли оплата этого занятия и был ли на нём ученик.
   *
   * ⚠️ **Полей НЕТ в ответе ученику вовсе**, как у `override` и `warnings`.
   * Это записи преподавателя о паре, и ученику они не адресуемы ни при каких
   * обстоятельствах: «не пришёл» и «не заплатил» это оценка, которую вторая
   * сторона получать не должна ни строкой в JSON, ни цветом в сетке.
   *
   * ⚠️ Полей нет и у преподавателя, который учёт не ведёт: отсутствие означает
   * «этот вопрос здесь не задают», а `paid: false` означает «занятие не
   * отмечено». Путать эти два ответа нельзя, поэтому это не булево с
   * умолчанием, а отсутствие ключа.
   *
   * ⚠️ Отметка держится на КЛЮЧЕ ЗАНЯТИЯ, а не на уроке, и живёт даже у
   * занятия, которое уроком так и не стало: в режиме сосуществования (Zoom по
   * внешней ссылке) уроков не создаётся вовсе. Разбор в
   * `./student-profile.ts`, `OccurrenceKey`.
   */
  paid: z.boolean().optional(),
  /**
   * ⚠️ Литералы продублированы из `Attendance` (`./student-profile.ts`)
   * НАМЕРЕННО, и это единственное место, где они повторены. Прямой импорт
   * замкнул бы цикл модулей (`student-profile.ts` берёт отсюда `LocalDate`), а
   * цикл в ESM означает не предупреждение, а `ReferenceError` на инициализации
   * zod-схемы в мёртвой зоне. Расхождение двух списков ловит тест
   * «словарь посещаемости в сетке совпадает с `Attendance`».
   */
  attendance: z.enum(["present", "absent"]).nullable().optional(),
  /**
   * Месяц этого занятия и его оплата, у помесячной пары.
   *
   * ⚠️ **Поля НЕТ в ответе ученику вовсе**, ровно как `paid` и `attendance`
   * выше, и правило стоит там же, в единственном месте сборки строки
   * (`marksOf`): «за сентябрь не заплатили» это запись преподавателя о паре, и
   * второй стороне она не адресуема ни строкой в JSON, ни цветом в сетке.
   *
   * ⚠️ Поля нет и у преподавателя, который учёт оплат не ведёт, и у пары с
   * режимом `per_lesson` или `none`: отсутствие означает «этот вопрос здесь не
   * задают», а `month.paid === false` означает «месяц не отмечен». Путать эти
   * два ответа нельзя, поэтому это не объект с умолчанием, а отсутствие ключа.
   *
   * ⚠️ Месяц посчитан в зоне СМОТРЯЩЕГО, то есть в той же, в которой нарисована
   * сама сетка: чип стоит на карточке занятия, и месяц, не совпадающий с
   * колонкой, под которой занятие нарисовано, читался бы как ошибка. Разбор
   * цены этого решения в `marksOf` (`apps/api/src/schedule-grid.ts`).
   *
   * ⚠️ Поля нет и у занятия ВНЕ окна чипов карточки ученика (`MONTHS_BACK` и
   * `MONTHS_FORWARD`), то есть дальше трёх месяцев назад и двух вперёд: сетка
   * листается в любой год, а месяц, отмеченный из неё, надо ещё уметь снять, и
   * снимается он чипами карточки. За тем же порогом ручка месяцев и сама
   * отвечает отказом.
   *
   * ⚠️ Поля нет и у ЗАГЛУШКИ перенесённого занятия (`state: "moved_away"`),
   * хотя поурочный `paid` у неё есть: отметка занятия одна на обе строки
   * переноса, а месяц у них разный, и занятие, переехавшее через границу
   * месяца, показало бы два чипа с разными месяцами.
   */
  month: ScheduleEntryMonth.optional(),
  /**
   * Разовое занятие создано записью ученика на окно (`docs/booking-design.md`,
   * 3.1): пометка «записался сам».
   *
   * ⚠️ **Поле только у ПРЕПОДАВАТЕЛЯ этой строки** и только у разового
   * занятия. Роль считается по строке, а не по типу аккаунта: человек может и
   * учить, и учиться.
   */
  selfBooked: z.boolean().optional(),
  /**
   * Ученик может отменить эту запись: урок создан ЕГО записью и ещё не начался.
   *
   * ⚠️ **Поле только у УЧЕНИКА этой строки** и только у разового занятия. Не
   * утечка: это его собственная запись. Сервер при отмене проверяет то же самое
   * заново, поле нужно клиенту, чтобы решить, где рисовать кнопку.
   */
  cancellableByMe: z.boolean().optional(),
  /**
   * Показывать ли поле «Слова занятия» (8.3 `docs/flashcards-design.md`).
   *
   * ⚠️ **Поле только у ПРЕПОДАВАТЕЛЯ этой строки**, и только когда предмет пары
   * английский или не задан, а у самого преподавателя английский есть
   * (`wordsFieldShown`, `teachesEnglish`). Предмета пары в ответе сетки нет и не
   * будет: это поле анкеты, поэтому сервер отдаёт готовый признак. На свёрнутой
   * строке группы поля нет.
   */
  wordsField: z.boolean().optional(),
  /**
   * Сколько живых карточек привязано к занятию, «Слова занятия: N» (8.3).
   *
   * ⚠️ **Поле только у УЧЕНИКА этой строки**, только когда колода видна ему и N
   * больше нуля; от предмета не зависит. Преподаватель число видит в самом поле.
   */
  wordsCount: z.number().int().min(1).optional(),
});
export type ScheduleEntry = z.infer<typeof ScheduleEntry>;

/**
 * Окно записи, как его видит ПРЕПОДАВАТЕЛЬ окна (`docs/booking-design.md`,
 * 3.1). Только открытые и будущие.
 *
 * `covered` означает «поверх окна стоит ваше занятие»: окно не гаснет само,
 * закроется на первой попытке записи (1.4 проекта). Считается по занятости
 * преподавателя, поэтому ученику это поле не уезжает ни в каком виде.
 */
export const MyOpening = z.object({
  id: z.string().uuid(),
  /** Момент начала, миллисекунды эпохи, как `ScheduleEntry.at`. */
  startsAt: z.number(),
  durationMin: z.number().int(),
  externalCallUrl: ExternalCallUrlView.nullable(),
  covered: z.boolean(),
});
export type MyOpening = z.infer<typeof MyOpening>;

/**
 * Окно записи, как его видит УЧЕНИК с живой связью (1.4 проекта).
 *
 * ⚠️ **Полей здесь ровно столько, сколько нужно, чтобы записаться**, и ни
 * одного про занятость преподавателя: видимость окна от неё не зависит вовсе,
 * иначе пустота в расписании рассказывала бы, когда у преподавателя другие
 * ученики (раздел 7 `docs/schedule-design.md`).
 */
export const BookableOpening = z.object({
  id: z.string().uuid(),
  startsAt: z.number(),
  durationMin: z.number().int(),
  teacherName: z.string(),
});
export type BookableOpening = z.infer<typeof BookableOpening>;

/**
 * Параметры окна.
 *
 * Все три необязательны, и у каждого своя причина. Даты: умолчание это неделя
 * вперёд, чтобы адрес `/v1/schedule` без хвоста отвечал осмысленным, а не
 * отказом. Зона: она нужна только тем, у кого пояс в профиле не подтверждён, а
 * у подтвердивших параметр игнорируется вовсе.
 */
export const ScheduleQuery = z.object({
  from: LocalDate.optional(),
  to: LocalDate.optional(),
  tz: TimeZoneName.optional(),
});
export type ScheduleQuery = z.infer<typeof ScheduleQuery>;

export const ScheduleResponse = z.object({
  /** Границы окна, локальные даты в зоне смотрящего, обе включительно. */
  from: LocalDate,
  to: LocalDate,
  /** Зона, в которой посчитаны границы и в которой клиент рисует время. */
  timeZone: TimeZoneName,
  /** Откуда взялась зона: из профиля или из параметра запроса. */
  timeZoneSource: z.enum(["profile", "query"]),
  /**
   * Часы сервера. Состояния посчитаны по ним, и клиенту они нужны, чтобы не
   * пересчитывать «сейчас» своими, сбитыми на десятки минут.
   */
  serverNow: z.number(),
  entries: z.array(ScheduleEntry),
  /** Строк было больше потолка, список обрезан. */
  truncated: z.boolean(),
  /** Окна записи, где я преподаватель: открытые и будущие, в окне сетки. */
  myOpenings: z.array(MyOpening),
  /**
   * Окна моих преподавателей, на которые я могу записаться. Без окон, которые
   * пересекаются с моими же занятиями в этом ответе (1.4 проекта, условие 4).
   */
  bookableOpenings: z.array(BookableOpening),
});
export type ScheduleResponse = z.infer<typeof ScheduleResponse>;

/**
 * Локальное время этого же занятия неделю назад в зоне смотрящего, если оно
 * отличается от нынешнего.
 *
 * ⚠️ Считается по правилу, а не по соседней строке ответа: занятие неделю
 * назад может лежать за границей окна, и тогда сравнивать было бы не с чем.
 * Если неделю назад правило занятия не производило (слот тогда ещё не начался
 * или стоял на другом дне), пометки нет: сравнивать не с чем по существу.
 */
export function weekEarlierShift(args: {
  series: ScheduleSeriesRule;
  slot: ScheduleSlotRule;
  occursOn: string;
  at: number;
  viewerZone: string;
}): string | null {
  const earlier = addLocalDays(args.occursOn, -7);
  if (!slotProducesDate(args.series, args.slot, earlier)) return null;
  const prev = localToInstant(earlier, args.slot.startsAt, args.series.timeZone).at;
  const was = localTimeAt(prev, args.viewerZone);
  return was === localTimeAt(args.at, args.viewerZone) ? null : was;
}

/* ──────────────────────── раскладка календаря ────────────────────────────── */

/**
 * Рабочее окно по умолчанию, минуты от полуночи. Диапазон часов календаря им
 * только НАЧИНАЕТСЯ: дальше он расширяется до охвата всех занятий недели.
 */
export const CALENDAR_DEFAULT_FROM_MIN = 8 * 60;
export const CALENDAR_DEFAULT_TO_MIN = 22 * 60;

/**
 * Минимальная нарисованная высота занятия в минутах.
 *
 * Занятие на пятнадцать минут иначе становится полоской, в которую не влезает
 * ни время, ни имя. Величина участвует и в раскладке дорожек: два коротких
 * занятия подряд, нарисованные с этой высотой, налезают друг на друга, и
 * честнее развести их по дорожкам, чем позволить им перекрыться.
 */
export const CALENDAR_MIN_SLOT_MIN = 30;

/**
 * Кусок занятия внутри одних местных суток.
 *
 * Кусков бывает два: занятие, переходящее местную полночь смотрящего, обязано
 * попасть в обе колонки. Двадцатидвухчасовое занятие на 90 минут это 22:00-24:00
 * в своём дне и 00:00-00:30 в следующем, и рисовать его одной коробкой,
 * вылезающей за низ колонки, значит врать про второй день.
 */
export interface CalendarPiece {
  /** Ключ куска: ключ строки плюс дата, потому что кусков у строки бывает два. */
  key: string;
  /** Ключ строки сетки, из которой вырос кусок. */
  entryKey: string;
  /** Локальная дата колонки, в зоне СМОТРЯЩЕГО. */
  date: string;
  startMin: number;
  endMin: number;
  /**
   * Кусок занимает место в раскладке.
   *
   * ⚠️ Отменённое и уехавшее занятие места не занимает, ровно как в счёте
   * наложений на сервере. Иначе отменённое занятие вечно ужимало бы живое вдвое,
   * то есть отмена ухудшала бы картину вместо того, чтобы её освобождать.
   */
  solid: boolean;
  /** Кусок это продолжение занятия с прошлых суток. */
  continues: boolean;
}

export interface PlacedCalendarPiece extends CalendarPiece {
  /** Номер дорожки внутри своего кластера наложений, от нуля. */
  lane: number;
  /** Сколько дорожек в кластере: из этих двух чисел и получается ширина. */
  lanes: number;
}

/**
 * Строки сетки в куски календаря.
 *
 * ⚠️ Всё считается в зоне СМОТРЯЩЕГО и только в ней. Занятие приезжает моментом,
 * а серия живёт в зоне преподавателя: раскладывать по дням «как в браузере»
 * значит положить занятие не в тот день у того, кто сидит по другую сторону
 * полуночи. Резать по полуночам умеет `splitAtLocalMidnights`, и второй
 * реализации не заводим: она уже написана для проверки окон доступности.
 */
export function calendarPieces(
  entries: readonly {
    key: string;
    at: number;
    durationMin: number | null;
    state: OccurrenceLifecycle | null;
  }[],
  zone: string,
): CalendarPiece[] {
  const out: CalendarPiece[] = [];
  for (const e of entries) {
    // Длительности нет у осиротевшего урока, чей слот уже не найти. Рисовать
    // его всё равно надо, поэтому берём минимальную высоту: это честнее
    // подставленного часа, которого никто не назначал
    const minutes = Math.max(e.durationMin ?? CALENDAR_MIN_SLOT_MIN, 1);
    const solid = e.state !== "cancelled" && e.state !== "moved_away";
    const pieces = splitAtLocalMidnights(e.at, minutes, zone);
    for (let i = 0; i < pieces.length; i += 1) {
      const p = pieces[i];
      if (p === undefined) continue;
      out.push({
        key: `${e.key}@${p.date}`,
        entryKey: e.key,
        date: p.date,
        startMin: p.startMin,
        endMin: p.endMin,
        solid,
        continues: i > 0,
      });
    }
  }
  return out;
}

/**
 * Диапазон часов календаря.
 *
 * ⚠️ Жёсткие 8-22 однажды спрячут занятие в 7 утра, а сутки целиком дают
 * шестнадцать пустых часов над содержимым. Поэтому диапазон ВЫВОДИТСЯ из самих
 * занятий: рабочее окно по умолчанию, расширенное до охвата всех кусков, с
 * округлением наружу до часа. Спрятаться не может ни одно занятие, и это
 * свойство построения, а не проверка, которую можно забыть.
 */
export function calendarHourRange(
  pieces: readonly CalendarPiece[],
  /**
   * Минута, которую диапазон обязан накрыть помимо занятий: отметка «сейчас».
   *
   * ⚠️ Без неё отметка пряталась бы ровно там, где она нужнее всего. Диапазон
   * выводится из занятий, а в семь утра занятий ещё нет ни одного: окно
   * начиналось бы с 8:00, и полоса текущего времени оказалась бы над верхним
   * краем календаря. То же вечером после последнего занятия.
   */
  alsoMin: number | null = null,
): {
  fromMin: number;
  toMin: number;
} {
  let from = CALENDAR_DEFAULT_FROM_MIN;
  let to = CALENDAR_DEFAULT_TO_MIN;
  for (const p of pieces) {
    from = Math.min(from, p.startMin);
    to = Math.max(to, Math.max(p.endMin, p.startMin + CALENDAR_MIN_SLOT_MIN));
  }
  if (alsoMin !== null) {
    from = Math.min(from, alsoMin);
    to = Math.max(to, alsoMin);
  }
  return {
    fromMin: Math.max(0, Math.floor(from / 60) * 60),
    toMin: Math.min(24 * 60, Math.ceil(to / 60) * 60),
  };
}

/**
 * Раскладка наложений по дорожкам, как в Outlook.
 *
 * Кластер это связная по перекрытию группа: пока очередной кусок начинается
 * раньше, чем кончается самый поздний из уже набранных, он в том же кластере.
 * Внутри кластера кусок жадно встаёт в первую дорожку, где он никого не
 * задевает, а ширина всех кусков кластера это одна дорожка из общего числа.
 *
 * ⚠️ Сравниваются НАРИСОВАННЫЕ границы, то есть с учётом минимальной высоты.
 * Иначе два занятия по пятнадцать минут подряд легли бы в одну дорожку и
 * перекрыли друг друга на экране, хотя по времени не пересекаются.
 *
 * ⚠️ Куски с `solid: false` в кластерах не участвуют вовсе и получают
 * `lanes: 0`: отменённое занятие рисуется поверх узкой полосой у края колонки.
 */
export function packLanes(pieces: readonly CalendarPiece[]): PlacedCalendarPiece[] {
  const out: PlacedCalendarPiece[] = [];

  const byDate = new Map<string, CalendarPiece[]>();
  for (const p of pieces) {
    if (!p.solid) {
      out.push({ ...p, lane: 0, lanes: 0 });
      continue;
    }
    const list = byDate.get(p.date);
    if (list === undefined) byDate.set(p.date, [p]);
    else list.push(p);
  }

  const drawnEnd = (p: CalendarPiece): number =>
    Math.max(p.endMin, p.startMin + CALENDAR_MIN_SLOT_MIN);

  for (const list of byDate.values()) {
    list.sort((a, b) => a.startMin - b.startMin || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));

    let cluster: CalendarPiece[] = [];
    let clusterEnd = -1;

    const flush = (): void => {
      if (cluster.length === 0) return;
      /** Конец последнего куска каждой дорожки. Длина массива это число дорожек. */
      const laneEnds: number[] = [];
      const placed: { piece: CalendarPiece; lane: number }[] = [];
      for (const p of cluster) {
        let lane = laneEnds.findIndex((end) => end <= p.startMin);
        if (lane === -1) {
          lane = laneEnds.length;
          laneEnds.push(drawnEnd(p));
        } else {
          laneEnds[lane] = drawnEnd(p);
        }
        placed.push({ piece: p, lane });
      }
      for (const { piece, lane } of placed) {
        out.push({ ...piece, lane, lanes: laneEnds.length });
      }
      cluster = [];
      clusterEnd = -1;
    };

    for (const p of list) {
      if (cluster.length > 0 && p.startMin >= clusterEnd) flush();
      cluster.push(p);
      clusterEnd = Math.max(clusterEnd, drawnEnd(p));
    }
    flush();
  }

  return out;
}

/** Смена часового пояса в профиле. Только своего: чужой правит его владелец. */
export const SetTimeZoneRequest = z.object({
  timeZone: TimeZoneName,
});
export type SetTimeZoneRequest = z.infer<typeof SetTimeZoneRequest>;

/**
 * Серии, чья зона разошлась с зоной профиля.
 *
 * ⚠️ Разошлась ПО ПОВЕДЕНИЮ, а не по строке. Строка сравнивается только ради
 * дешёвого быстрого пути: представитель зоны может поменяться между версиями
 * ICU, и тогда та же самая зона выглядела бы другой.
 *
 * Что с этим делать, решает человек кнопкой «перевести серии». Молча мы не
 * трогаем ни одной серии: смена пояса в профиле не имеет права двигать уже
 * согласованные занятия.
 */
export const TimeZoneDriftResponse = z.object({
  /** Зона профиля. `null` означает «человек ещё не подтверждал». */
  profileTimeZone: TimeZoneName.nullable(),
  /** Серии, у которых зона ведёт себя иначе, чем зона профиля. */
  series: z.array(
    z.object({
      id: SeriesId,
      timeZone: TimeZoneName,
      counterpartName: z.string().nullable(),
      /**
       * Поведение зоны серии совпадает с зоной профиля на окне плюс-минус год.
       * Тогда перевод не сдвинет ни одного занятия, и спрашивать особо не о
       * чем; при расхождении сдвинет, и спросить обязательно.
       */
      behavesSame: z.boolean(),
    }),
  ),
});
export type TimeZoneDriftResponse = z.infer<typeof TimeZoneDriftResponse>;
